/**
 * 게시물 관련 컴포넌트
 * - 게시물 생성, 표시, 수정, 삭제
 * - 게시물 좋아요, 댓글
 */
import { ref, push, set, onValue, remove, update, get, query, orderByChild, limitToLast, off } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, database, storage } from '../utils/firebase-config.js';
import { store } from '../store/index.js';
import { addPost, setPosts, updatePost, deletePost, likePost, unlikePost, addComment, deleteComment } from '../store/actions/postActions.js';
import { showToast, showLoading, formatDate } from '../utils/ui-manager.js';
import { handleNetworkRequest } from '../utils/network-status.js';
import { getMessaging } from 'firebase/messaging';
import { DraggablePanel, PanelManager } from './draggable-panel.js';
import { getUserFCMTokens } from '../utils/firebase-messaging.js';
import { collection, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

// 패널 매니저 인스턴스 생성
const panelManager = window.panelManager || new PanelManager();
window.panelManager = panelManager;

/**
 * 애플리케이션 버전 표시 컴포넌트
 */
import { APP_VERSION, BUILD_DATE } from '../version.js';

/**
 * 기본 프로필 이미지 경로
 */
const DEFAULT_PROFILE_IMAGE = '/assets/img/default-profile.png';

/**
 * 보호된 경로 패턴 - 삭제를 방지할 경로들
 */
const PROTECTED_PATHS = [
  /^users\/[^\/]+\/devices$/,  // users/{uid}/devices 경로 보호
  /^users\/[^\/]+\/profile$/,  // users/{uid}/profile 경로 보호  
  /^users\/[^\/]+\/fcmTokens$/ // users/{uid}/fcmTokens 경로 보호
];

/**
 * 안전한 remove 함수 - 보호된 경로 확인 후 삭제
 * @param {DatabaseReference} databaseRef - Firebase 데이터베이스 참조
 * @param {string} operation - 작업 설명 (로깅용)
 * @returns {Promise<boolean>} 삭제 성공 여부
 */
const safeRemove = async (databaseRef, operation = '알 수 없는 작업') => {
  try {
    // 경로 추출
    const path = databaseRef.toString().split('.com/')[1] || '';
    
    console.log(`[SAFE_REMOVE] 삭제 시도: ${path} (작업: ${operation})`);
    
    // 보호된 경로인지 확인
    const isProtected = PROTECTED_PATHS.some(pattern => pattern.test(path));
    
    if (isProtected) {
      console.error(`[SAFE_REMOVE] 보호된 경로 삭제 시도 차단: ${path}`);
      showToast(`보호된 데이터 삭제가 차단되었습니다: ${path}`, 'warning');
      return false;
    }
    
    // users/{uid}/devices 하위 경로는 허용하되 정확한 경로만 허용
    if (path.includes('/devices/') && !path.match(/^users\/[^\/]+\/devices\/[^\/]+$/)) {
      console.warn(`[SAFE_REMOVE] 의심스러운 devices 경로 삭제 시도: ${path}`);
    }
    
    // 실제 삭제 수행
    await remove(databaseRef);
    console.log(`[SAFE_REMOVE] 삭제 완료: ${path}`);
    return true;
    
  } catch (error) {
    console.error(`[SAFE_REMOVE] 삭제 실패 (${operation}):`, error);
    throw error;
  }
};

/**
 * 이미지 로드 오류 처리 함수 - 기본 이미지로 대체
 * @param {Event} error - 이미지 오류 이벤트
 */
const handleImageError = (error) => {
  if (error && error.target) {
    console.warn('이미지 로딩 실패, 기본 이미지로 대체:', error.target.src);
    error.target.src = DEFAULT_PROFILE_IMAGE;
    // 추가 오류 방지
    error.target.onerror = null;
  }
};

/**
 * 프로필 이미지 HTML 생성
 * @param {string} photoURL - 프로필 이미지 URL
 * @param {string} displayName - 사용자 이름
 * @param {string} className - 적용할 CSS 클래스
 * @returns {string} 이미지 HTML
 */
const createProfileImageHTML = (photoURL, displayName = '사용자', className = 'avatar') => {
  return `
    <img 
      src="${photoURL || DEFAULT_PROFILE_IMAGE}" 
      alt="${displayName}" 
      class="${className}" 
      onerror="this.onerror=null; this.src='${DEFAULT_PROFILE_IMAGE}';"
      style="width: 25px; height: 25px; object-fit: cover; border-radius: 50%;"
    >
  `;
};

// 페이지 로드 시 이미지 오류 처리 함수 등록
document.addEventListener('DOMContentLoaded', () => {
  // 기존 이미지에 오류 핸들러 등록
  document.querySelectorAll('img').forEach(img => {
    img.onerror = handleImageError;
  });
});

/**
 * 게시물 생성 함수
 * @param {string} content - 게시물 내용
 * @param {File} imageFile - 이미지 파일 (선택적)
 */
export const createPost = async (content, imageFile = null) => {
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  try {
    showLoading(true, 'posts-container', '게시물을 생성 중입니다...');
    
    // 사용자 이메일 가져오기
    const userEmail = auth.currentUser.email;
    
    const postData = {
      userId: auth.currentUser.uid,
      content: content,
      author: {
        displayName: auth.currentUser.displayName,
        photoURL: auth.currentUser.photoURL,
        uid: auth.currentUser.uid,
        email: userEmail // 이메일 정보 추가
      },
      likes: [],
      likeCount: 0,
      comments: [],
      commentCount: 0,
      createdAt: new Date().toISOString()
    };
    
    // 이미지 업로드 처리
    if (imageFile) {
      try {
        console.log('이미지 처리 시작:', imageFile.name);
        
        // 이미지 메타데이터 저장
        postData.imageMetadata = {
          name: imageFile.name,
          type: imageFile.type,
          size: imageFile.size,
          lastModified: imageFile.lastModified
        };

        // 이미지 크기가 300KB 이상인 경우 사용자에게 확인
        if (imageFile.size > 300 * 1024) {
          const sizeInMB = (imageFile.size / (1024 * 1024)).toFixed(2);
          const confirmMessage = `이미지 크기가 300KB를 초과합니다 (${sizeInMB} MB). 이미지 로딩이 느릴 수 있습니다. 계속 진행하시겠습니까?`;
          
          if (!confirm(confirmMessage)) {
            showToast('이미지 업로드가 취소되었습니다.', 'info');
            showLoading(false, 'posts-container');
            return;
          }
        }
        
        // 이미지 용량 확인 (1MB 이하로 제한 - Base64는 용량이 약 33% 증가함)
        if (imageFile.size > 1 * 1024 * 1024) {
          throw new Error('Base64 저장 방식에서는 이미지 크기는 1MB 이하여야 합니다.');
        }

        // 먼저 Firebase Storage 업로드 시도
        try {
          const imageFileName = `${Date.now()}-${imageFile.name}`;
          const imageUploadRef = storageRef(storage, `posts/images/${imageFileName}`);
          
          console.log('Firebase Storage 업로드 시도:', imageFileName);
          await uploadBytes(imageUploadRef, imageFile);
          console.log('이미지 업로드 성공');
          
          const imageURL = await getDownloadURL(imageUploadRef);
          postData.imageURL = imageURL;
          postData.imageStorageType = 'firebase';
          
          console.log('이미지 URL 획득 성공:', imageURL);
        } catch (storageError) {
          console.warn('Firebase Storage 업로드 실패, Base64로 대체:', storageError);
          
          // Firebase Storage 업로드 실패 시 Base64로 대체
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              // Base64 데이터 URL을 postData에 저장
              postData.imageURL = reader.result;
              postData.imageStorageType = 'base64';
              console.log('이미지를 Base64로 변환하여 저장');
              resolve();
            };
            reader.readAsDataURL(imageFile);
          }).then(() => {
            // Base64 변환 완료 후 계속 진행
            console.log('Base64 변환 완료, 게시물 저장 계속 진행');
          });
        }
      } catch (uploadError) {
        console.error('이미지 처리 중 오류:', uploadError);
        
        // 이미지 크기 제한 오류
        if (uploadError.message && uploadError.message.includes('이미지 크기')) {
          showToast(uploadError.message, 'error');
          throw uploadError;
        }
        
        // CORS 오류 감지
        if (uploadError.message && (
            uploadError.message.includes('CORS') || 
            uploadError.message.includes('XMLHttpRequest') ||
            uploadError.message.includes('Network Error'))) {
          showToast('이미지 업로드 중 CORS 오류가 발생했습니다. 텍스트만 게시됩니다.', 'warning');
        } else {
          // 일반 업로드 오류는 계속 진행하되 이미지 없이 게시물 작성
          showToast('이미지 업로드에 실패했습니다. 텍스트만 게시됩니다.', 'warning');
        }
      }
    }
    
    // Firebase에 게시물 저장
    const postsRef = ref(database, 'posts');
    const newPostRef = push(postsRef);
    const postId = newPostRef.key;
    postData.id = postId;
    
    // 게시물 데이터 저장
    await set(newPostRef, postData);
    
    // 권한 관리 구조 추가 - 사용자 권한 저장
    const userPermissionRef = ref(database, `posts/${postId}/users/owners/${userEmail.replace(/\./g, ',')}`);
    await set(userPermissionRef, {
      email: userEmail,
      role: 'owner',
      displayName: auth.currentUser.displayName || '사용자',
      addedAt: new Date().toISOString()
    });
    
    console.log(`게시물 생성: ${postId}, 소유자 권한 추가: ${userEmail}`);
    
    // Redux 스토어에 게시물 추가
    store.dispatch(addPost(postData));
    
    showToast('게시물이 성공적으로 작성되었습니다.', 'success');
  } catch (error) {
    console.error('게시물 생성 중 오류 발생:', error);
    
    // 오류 메시지 맞춤화
    if (error.message && error.message.includes('CORS')) {
      showToast('CORS 정책 위반: Firebase Storage 설정을 확인해주세요.', 'error');
    } else {
      showToast('게시물 생성 중 오류가 발생했습니다: ' + error.message, 'error');
    }
  } finally {
    showLoading(false, 'posts-container');
  }
};

/**
 * 게시물 목록 불러오기 및 표시
 * @param {number} limit - 불러올 게시물 수 (기본값: 10)
 */
export const renderPosts = async (limit = 10) => {
  try {
    showLoading(true, 'posts-container', '게시물을 불러오는 중입니다...');
    
    // 오프라인/온라인 상태 처리
    await handleNetworkRequest(
      // 온라인 콜백
      async () => {
        const postsRef = ref(database, 'posts');
        
        // 실시간 데이터베이스 리스너 설정
        onValue(postsRef, async (snapshot) => {
          if (snapshot.exists()) {
            const postsData = [];
            snapshot.forEach((childSnapshot) => {
              postsData.push(childSnapshot.val());
            });
            
            // 최신 게시물이 위로 오도록 정렬
            postsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            // 리덕스 상태 업데이트
            store.dispatch(setPosts(postsData));
            
            // 게시물 렌더링
            await renderPostsToDOM(postsData);
          } else {
            // 게시물이 없을 경우
            renderEmptyPostsMessage();
          }
          
          showLoading(false, 'posts-container');
        });
      },
      // 오프라인 콜백
      async () => {
        // 오프라인 상태에서는 리덕스 스토어에서 캐시된 데이터 사용
        const cachedPosts = store.getState().posts.posts;
        
        if (cachedPosts && cachedPosts.length > 0) {
          await renderPostsToDOM(cachedPosts);
          showToast('오프라인 모드에서 캐시된 게시물을 표시합니다.', 'info');
        } else {
          renderEmptyPostsMessage('오프라인 상태입니다. 데이터를 불러올 수 없습니다.');
        }
        
        showLoading(false, 'posts-container');
      }
    );
  } catch (error) {
    console.error('게시물 로드 중 오류 발생:', error);
    showToast('게시물을 불러오는 중 오류가 발생했습니다.', 'error');
    showLoading(false, 'posts-container');
  }
};

/**
 * 게시물을 DOM에 렌더링하는 함수
 * @param {Array} posts - 게시물 배열
 */
const renderPostsToDOM = async (posts) => {
  const postsContainer = document.getElementById('posts-container');
  
  if (!postsContainer) {
    console.error('게시물 컨테이너를 찾을 수 없습니다.');
    return;
  }
  
  postsContainer.innerHTML = '';
  const currentUser = auth.currentUser;
  
  // 권한이 있는 게시물 필터링
  const filteredPosts = currentUser ? posts.filter(post => {
    // 사용자 이메일 포맷 (Firebase 경로용)
    const userEmail = currentUser.email;
    const userEmailForPath = userEmail.replace(/\./g, ',');
    
    // 권한 확인
    const isAuthor = post.author && post.author.email === userEmail;
    const isOwnerByUid = post.userId === currentUser.uid;
    const hasOwnerPermission = post.users && post.users.owners && post.users.owners[userEmailForPath];
    const hasManagerPermission = post.users && post.users.managers && post.users.managers[userEmailForPath];
    const hasViewerPermission = post.users && post.users.viewers && post.users.viewers[userEmailForPath];
    const hasAdminPermission = post.users && post.users.admins && post.users.admins[userEmailForPath];
    
    // 게시물에 대한 권한이 하나라도 있으면 true 반환
    return isAuthor || isOwnerByUid || hasOwnerPermission || hasManagerPermission || hasViewerPermission || hasAdminPermission;
  }) : posts; // 로그인하지 않은 경우 모든 게시물 표시
  
  // 필터링된 게시물이 없으면 메시지 표시
  if (filteredPosts.length === 0) {
    renderEmptyPostsMessage('볼 수 있는 권한이 있는 게시물이 없습니다.');
    return;
  }
  
  // 게시물 렌더링 - 각 게시물마다 디바이스 정보를 가져와서 렌더링
  for (const post of filteredPosts) {
    // 날짜 형식 변환
    const postDate = new Date(post.createdAt);
    const formattedDate = postDate.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // 좋아요 및 댓글 데이터 처리
    let likes = post.likes || [];
    if (!Array.isArray(likes) && typeof likes === 'object') {
      likes = Object.values(likes);
    }
    
    let comments = post.comments || [];
    if (!Array.isArray(comments) && typeof comments === 'object') {
      comments = Object.values(comments);
    }
    
    // 현재 사용자가 좋아요를 눌렀는지 확인
    const hasLiked = currentUser && likes.some(like => 
      (typeof like === 'string' && like === currentUser.uid) || 
      (typeof like === 'object' && like.userId === currentUser.uid)
    );
    
    // 게시물 HTML 생성
    const postElement = document.createElement('div');
    postElement.classList.add('card', 'mb-3', 'post-card');
    postElement.dataset.postId = post.id;
    
    const cardBody = document.createElement('div');
    cardBody.classList.add('card-body', 'post');
    
    // 현재 사용자의 권한 확인
    let userRole = '';
    let roleBadgeClass = '';
    
    if (currentUser) {
      // 이메일 포맷 통일 (Firebase 경로용 콤마 대체 형식을 원래 이메일로 변환)
      const userEmail = currentUser.email;
      const userEmailForPath = userEmail.replace(/\./g, ',');
      
      if (post.users && post.users.owners && post.users.owners[userEmailForPath]) {
        userRole = 'owner';
        roleBadgeClass = 'bg-success';
      } else if (post.author && post.author.email === userEmail) {
        // 게시물 작성자도 owner로 처리 (혹시 권한 데이터가 없는 경우)
        userRole = 'owner';
        roleBadgeClass = 'bg-success';
      } else if (post.users && post.users.managers && post.users.managers[userEmailForPath]) {
        userRole = 'manager';
        roleBadgeClass = 'bg-primary';
      } else if (post.users && post.users.viewers && post.users.viewers[userEmailForPath]) {
        userRole = 'viewer';
        roleBadgeClass = 'bg-info';
      } else if (post.users && post.users.admins && post.users.admins[userEmailForPath]) {
        userRole = 'admin';
        roleBadgeClass = 'bg-danger';
      } else if (currentUser.uid === post.userId) {
        // UID로 확인하는 대체 로직 (최신 버전 호환성)
        userRole = 'owner';
        roleBadgeClass = 'bg-success';
      }
      // 권한 정보가 없는 경우 기본적으로 viewer 권한 부여하는 로직 제거
    }
    
    // 권한 뱃지 HTML - 클릭 가능하도록 수정
    const isOwner = userRole === 'owner' || (currentUser && currentUser.uid === post.userId);
    const roleBadgeHTML = currentUser ? 
      `<span class="badge ${roleBadgeClass} ms-2 role-badge ${isOwner ? 'role-badge-clickable' : ''}" 
        data-post-id="${post.id}" data-role="${userRole}">
        ${userRole}
      </span>` : '';
    
    // 디바이스 목록 가져오기 (게시물 작성자의 디바이스)
    let deviceSelectHTML = '';
    try {
      const authorDevices = await getUserDevices(post.userId);
      if (authorDevices && authorDevices.length > 0) {
        // 선택창 HTML 생성
        deviceSelectHTML = `
          <select class="form-select form-select-sm device-selector ms-2" 
                  data-post-id="${post.id}" 
                  style="min-width: 100px; max-width: 150px;">
            <option value="content">본문</option>
            ${authorDevices.map((device, index) => 
              `<option value="${device}">장치${index + 1}</option>`
            ).join('')}
          </select>
        `;
      }
    } catch (error) {
      console.error('디바이스 목록 로딩 실패:', error);
    }
    
    // 헤더 생성
    const postHeader = document.createElement('div');
    postHeader.classList.add('post-header');
    postHeader.innerHTML = `
      ${createProfileImageHTML(post.author.photoURL, post.author.displayName, 'rounded-circle')}
      <div class="post-user-info">
        <div class="d-flex align-items-center">
          <h6 class="post-username mb-0">${post.author.displayName}</h6>
        </div>
        <small class="post-time">${formattedDate}</small>
      </div>
      <div class="ms-auto d-flex align-items-center">
        ${roleBadgeHTML}
        ${deviceSelectHTML}
      ${post.userId === (currentUser ? currentUser.uid : '') ? `
          <div class="dropdown ms-2">
          <button class="btn btn-sm btn-link text-muted" type="button" data-bs-toggle="dropdown">
            <i class="bi bi-three-dots-vertical"></i>
          </button>
          <ul class="dropdown-menu">
            <li><a class="dropdown-item edit-post-btn" href="#" data-post-id="${post.id}">수정</a></li>
            <li><a class="dropdown-item delete-post-btn" href="#" data-post-id="${post.id}">삭제</a></li>
          </ul>
        </div>
      ` : ''}
      </div>
    `;
    cardBody.appendChild(postHeader);
    
    // 본문 컨텐츠 영역을 탭 구조로 생성
    const postContentDiv = document.createElement('div');
    postContentDiv.classList.add('post-content'); // 드롭 핸들러 타겟 클래스
    
    // 탭 콘텐츠 컨테이너 생성 (탭 제목은 숨김)
    const tabContentContainer = document.createElement('div');
    tabContentContainer.classList.add('tab-content');
    tabContentContainer.id = `post-tab-content-${post.id}`;
    
    // 본문 탭 (기본 표시)
    const contentTabPane = document.createElement('div');
    contentTabPane.classList.add('tab-pane', 'fade', 'show', 'active');
    contentTabPane.id = `content-tab-${post.id}`;
    contentTabPane.setAttribute('role', 'tabpanel');
    
    // 포스트 내용 처리 - 패널 데이터 파싱
    let parsedContent = parseContentForPanels(post.content);
    contentTabPane.innerHTML = parsedContent;
    
    // 이미지 처리
    if (post.imageURL) {
      const postImage = document.createElement('img');
      postImage.src = post.imageURL;
      postImage.alt = '게시물 이미지';
      postImage.classList.add('post-image', 'img-fluid', 'rounded');
      if (post.imageStorageType === 'base64') {
        postImage.classList.add('base64-image');
        postImage.loading = 'lazy';
      }
      contentTabPane.appendChild(postImage);
      
      // 이미지 속성 정보를 접힌 상태로 표시
      if (post.imageMetadata) {
        const imageMetadata = post.imageMetadata;
        const imageInfoContainer = document.createElement('div');
        imageInfoContainer.classList.add('image-info-container', 'mt-2', 'mb-3');
        
        // 이미지 정보 접기/펼치기 버튼
        const imageInfoToggle = document.createElement('button');
        imageInfoToggle.classList.add('btn', 'btn-sm', 'btn-outline-secondary', 'w-100', 'd-flex', 'justify-content-between', 'align-items-center');
        imageInfoToggle.setAttribute('type', 'button');
        imageInfoToggle.setAttribute('data-bs-toggle', 'collapse');
        imageInfoToggle.setAttribute('data-bs-target', `#image-info-${post.id}`);
        imageInfoToggle.setAttribute('aria-expanded', 'false');
        imageInfoToggle.innerHTML = `
          <span><i class="bi bi-image me-2"></i>이미지 정보</span>
          <i class="bi bi-chevron-down"></i>
        `;
        
        // 접힌 상태의 이미지 정보 패널
        const imageInfoPanel = document.createElement('div');
        imageInfoPanel.classList.add('collapse');
        imageInfoPanel.id = `image-info-${post.id}`;
        
        // 이미지 크기 계산 (KB 또는 MB로 표시)
        let sizeDisplay = '';
        if (imageMetadata.size < 1024 * 1024) {
          sizeDisplay = `${Math.round(imageMetadata.size / 1024)} KB`;
        } else {
          sizeDisplay = `${(imageMetadata.size / (1024 * 1024)).toFixed(2)} MB`;
        }
        
        // 이미지 날짜 포맷팅
        const imageDate = new Date(imageMetadata.lastModified);
        const formattedImageDate = imageDate.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        // 이미지 정보 렌더링
        imageInfoPanel.innerHTML = `
          <div class="card card-body mt-2 bg-light">
            <ul class="list-group list-group-flush">
              <li class="list-group-item bg-light d-flex justify-content-between">
                <span>파일명:</span>
                <span class="text-truncate ms-2">${imageMetadata.name}</span>
              </li>
              <li class="list-group-item bg-light d-flex justify-content-between">
                <span>유형:</span>
                <span>${imageMetadata.type}</span>
              </li>
              <li class="list-group-item bg-light d-flex justify-content-between">
                <span>크기:</span>
                <span class="${imageMetadata.size > 300 * 1024 ? 'text-warning fw-bold' : ''}">${sizeDisplay}</span>
              </li>
              <li class="list-group-item bg-light d-flex justify-content-between">
                <span>수정일:</span>
                <span>${formattedImageDate}</span>
              </li>
              <li class="list-group-item bg-light d-flex justify-content-between">
                <span>저장방식:</span>
                <span>${post.imageStorageType === 'firebase' ? 'Firebase Storage' : 'Base64 인코딩'}</span>
              </li>
            </ul>
          </div>
        `;
        
        // 다크 모드 호환성을 위한 클래스 추가
        if (document.body.getAttribute('data-theme') === 'dark') {
          imageInfoPanel.querySelectorAll('.bg-light').forEach(el => {
            el.classList.remove('bg-light');
            el.classList.add('bg-dark', 'text-light');
          });
        }
        
        // 이미지 정보 컨테이너에 요소 추가
        imageInfoContainer.appendChild(imageInfoToggle);
        imageInfoContainer.appendChild(imageInfoPanel);
        contentTabPane.appendChild(imageInfoContainer);
      }
    }
    
    // 탭 컨테이너에 본문 탭 추가
    tabContentContainer.appendChild(contentTabPane);
    
    // 디바이스 탭들 생성 (처음에는 숨김)
    try {
      const authorDevices = await getUserDevices(post.userId);
      if (authorDevices && authorDevices.length > 0) {
        for (let i = 0; i < authorDevices.length; i++) {
          const deviceTabPane = document.createElement('div');
          deviceTabPane.classList.add('tab-pane', 'fade', 'device-content');
          deviceTabPane.id = `device-tab-${post.id}-${i}`;
          deviceTabPane.setAttribute('role', 'tabpanel');
          deviceTabPane.setAttribute('data-device-id', authorDevices[i]);
          deviceTabPane.innerHTML = `
            <div class="text-center p-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">로딩 중...</span>
              </div>
              <p class="mt-2 text-muted">장치 정보를 불러오는 중...</p>
            </div>
          `;
          
          tabContentContainer.appendChild(deviceTabPane);
        }
      }
    } catch (error) {
      console.error('디바이스 탭 생성 실패:', error);
    }
    
    // 컨텐츠 영역에 탭 컨테이너 추가
    postContentDiv.appendChild(tabContentContainer);
    
    // --- 저장된 임베드 패널 렌더링 시작 ---
    if (post.embeddedPanels && typeof post.embeddedPanels === 'object') {
      renderEmbeddedPanels(post.embeddedPanels, postElement, post.id);
    }
    // --- 저장된 임베드 패널 렌더링 끝 ---
    
    // --- 드롭 영역 설정 시작 ---
    postContentDiv.classList.add('droppable-post-content'); // 드롭 가능 식별 클래스 추가

    if (window.handlePostDragOver) {
      postContentDiv.addEventListener('dragover', window.handlePostDragOver);
    }
    if (window.handlePostDragLeave) {
      postContentDiv.addEventListener('dragleave', window.handlePostDragLeave);
    }
    if (window.handlePostDrop) {
      postContentDiv.addEventListener('drop', window.handlePostDrop);
    }
    // --- 드롭 영역 설정 끝 ---
    
    cardBody.appendChild(postContentDiv);
    
    // 액션 버튼 영역
    const postActions = document.createElement('div');
    postActions.classList.add('post-actions');
    postActions.innerHTML = `
      <button class="post-action-btn ${hasLiked ? 'like-btn' : ''}" data-post-id="${post.id}">
        <i class="bi ${hasLiked ? 'bi-heart-fill' : 'bi-heart'}"></i> <span>좋아요 ${post.likeCount || likes.length || 0}</span>
      </button>
      <button class="post-action-btn comment-btn" data-post-id="${post.id}">
        <i class="bi bi-chat"></i> <span>댓글 ${post.commentCount || comments.length || 0}</span>
      </button>
      <button class="post-action-btn share-btn" data-post-id="${post.id}">
        <i class="bi bi-share"></i> <span>공유</span>
      </button>
      <button class="post-action-btn chat-btn chat-btn-icon" data-post-id="${post.id}">
        <div class="chat-btn-content">
          <i class="bi bi-chat-dots"></i>
          <span class="chat-btn-text">실시간채팅</span>
        </div>
      </button>
    `;
    cardBody.appendChild(postActions);
    
    // 댓글 섹션
    const commentsSection = document.createElement('div');
    commentsSection.classList.add('comments-section', 'd-none');
    commentsSection.id = `comments-${post.id}`;
    commentsSection.innerHTML = `
      <div class="comments-list">
        ${renderComments(comments)}
      </div>
      <div class="new-comment">
        <img src="${currentUser ? currentUser.photoURL : 'assets/default-avatar.png'}" alt="댓글 작성자" class="rounded-circle" style="width: 32px; height: 32px;">
        <input type="text" class="comment-input" placeholder="댓글을 입력하세요..." data-post-id="${post.id}">
      </div>
    `;
    cardBody.appendChild(commentsSection);
    
    postElement.appendChild(cardBody);
    postsContainer.appendChild(postElement); // 생성된 요소를 컨테이너에 추가
  }
  
  // 이벤트 리스너 설정
  setupPostEventListeners();
};

/**
 * 댓글 목록 HTML 생성
 * @param {Array|Object} comments - 댓글 배열 또는 객체
 * @returns {string} 댓글 HTML
 */
const renderComments = (comments) => {
  if (!comments || comments.length === 0) {
    return '<p class="no-comments">아직 댓글이 없습니다. 첫 댓글을 작성해보세요!</p>';
  }
  
  return comments.map(comment => {
    const { id, content, author, createdAt, userId } = comment;
    const formattedDate = new Date(createdAt).toLocaleString('ko-KR');
    const isCurrentUser = auth.currentUser && userId === auth.currentUser.uid;
    
    // 프로필 사진 설정 (기본 이미지 포함)
    let photoURL = author?.photoURL || DEFAULT_PROFILE_IMAGE;
    
    return `
      <div class="comment" data-comment-id="${id}">
        <div class="comment-header">
          <div class="comment-author">
            <img 
              src="${photoURL}" 
              alt="${author?.displayName || '사용자'}" 
              class="avatar" 
              onerror="this.onerror=null; this.src='${DEFAULT_PROFILE_IMAGE}';"
              style="width: 22px; height: 22px; object-fit: cover; border-radius: 50%;"
            >
            <div>
              <span class="author-name">${author?.displayName || '사용자'}</span>
              <span class="comment-date">${formattedDate}</span>
          </div>
        </div>
          ${isCurrentUser ? 
            `<button class="delete-comment-btn" data-comment-id="${id}">
              <i class="bi bi-trash-fill"></i>
            </button>` : 
            ''}
        </div>
        <div class="comment-content">${content}</div>
      </div>
    `;
  }).join('');
};

/**
 * 빈 게시물 메시지 표시
 * @param {string} message - 표시할 메시지 (기본값: '게시물이 없습니다. 첫 게시물을 작성해보세요!')
 */
const renderEmptyPostsMessage = (message = '게시물이 없습니다. 첫 게시물을 작성해보세요!') => {
  const postsContainer = document.getElementById('posts-container');
  
  if (!postsContainer) {
    console.error('게시물 컨테이너를 찾을 수 없습니다.');
    return;
  }
  
  postsContainer.innerHTML = `
    <div class="text-center my-5 py-5">
      <i class="bi bi-journal-text fs-1 text-muted mb-3"></i>
      <h5 class="text-muted">${message}</h5>
    </div>
  `;
};

/**
 * 게시물 관련 이벤트 리스너 설정
 */
const setupPostEventListeners = () => {
  // 좋아요 버튼 클릭 이벤트
  document.querySelectorAll('.post-action-btn').forEach(button => {
    if (button.classList.contains('like-btn') || button.querySelector('.bi-heart') || button.querySelector('.bi-heart-fill')) {
      button.addEventListener('click', handleLikeButtonClick);
    }
  });
  
  // 댓글 버튼 클릭 이벤트
  document.querySelectorAll('.comment-btn').forEach(button => {
    button.addEventListener('click', handleCommentButtonClick);
  });
  
  // 댓글 입력 이벤트
  document.querySelectorAll('.comment-input').forEach(input => {
    input.addEventListener('keydown', handleCommentInputKeydown);
  });
  
  // 게시물 수정 버튼 클릭 이벤트
  document.querySelectorAll('.edit-post-btn').forEach(button => {
    button.addEventListener('click', handleEditPostClick);
  });
  
  // 게시물 삭제 버튼 클릭 이벤트
  document.querySelectorAll('.delete-post-btn').forEach(button => {
    button.addEventListener('click', handleDeletePostClick);
  });
  
  // 댓글 삭제 버튼 클릭 이벤트
  document.querySelectorAll('.delete-comment-btn').forEach(button => {
    // 기존 이벤트 리스너 제거 (중복 방지)
    button.removeEventListener('click', handleDeleteCommentClick);
    // 새 이벤트 리스너 추가
    button.addEventListener('click', handleDeleteCommentClick);
  });
  
  // 공유 버튼 클릭 이벤트
  document.querySelectorAll('.share-btn').forEach(button => {
    button.addEventListener('click', handleShareButtonClick);
  });

  // 실시간 채팅 버튼 클릭 이벤트
  document.querySelectorAll('.chat-btn').forEach(button => {
    button.addEventListener('click', handleChatButtonClick);
  });

  // 권한 뱃지 클릭 이벤트 추가
  document.querySelectorAll('.role-badge-clickable').forEach(badge => {
    badge.addEventListener('click', handleRoleBadgeClick);
  });

  // 디바이스 선택창 변경 이벤트 추가
  document.querySelectorAll('.device-selector').forEach(selector => {
    selector.addEventListener('change', handleDeviceSelectChange);
  });

  // 기존 코드...
};

/**
 * 좋아요 버튼 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleLikeButtonClick = async (event) => {
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  const button = event.currentTarget;
  const postId = button.getAttribute('data-post-id');
  const icon = button.querySelector('i');
  const hasLiked = icon.classList.contains('bi-heart-fill');
  
  try {
    const postRef = ref(database, `posts/${postId}`);
    
    // Firebase Realtime Database 업데이트
    onValue(postRef, async (snapshot) => {
      if (snapshot.exists()) {
        const post = snapshot.val();
        
        // likes 데이터 처리
        let likes = post.likes || [];
        if (!Array.isArray(likes) && typeof likes === 'object') {
          likes = Object.values(likes);
        } else if (!Array.isArray(likes)) {
          likes = [];
        }
        
        let likeCount = post.likeCount || 0;
        
        if (hasLiked) {
          // 좋아요 취소
          likes = likes.filter(like => {
            if (typeof like === 'string') {
              return like !== auth.currentUser.uid;
            } else if (typeof like === 'object') {
              return like.userId !== auth.currentUser.uid;
            }
            return true;
          });
          likeCount = Math.max(0, likeCount - 1);
          
          // Redux 스토어 업데이트
          store.dispatch(unlikePost(postId, auth.currentUser.uid));
          
          // UI 업데이트
          icon.classList.replace('bi-heart-fill', 'bi-heart');
          button.classList.remove('like-btn');
        } else {
          // 좋아요 추가
          const userLike = { 
            userId: auth.currentUser.uid,
            createdAt: new Date().toISOString()
          };
          
          const existingLike = likes.some(like => {
            if (typeof like === 'string') {
              return like === auth.currentUser.uid;
            } else if (typeof like === 'object') {
              return like.userId === auth.currentUser.uid;
            }
            return false;
          });
          
          if (!existingLike) {
            likes.push(userLike);
          }
          likeCount = likes.length;
          
          // Redux 스토어 업데이트
          store.dispatch(likePost(postId, auth.currentUser.uid));
          
          // UI 업데이트
          icon.classList.replace('bi-heart', 'bi-heart-fill');
          button.classList.add('like-btn');
        }
        
        // 좋아요 카운트 업데이트
        button.querySelector('span').textContent = `좋아요 ${likeCount}`;
        
        // Firebase에 업데이트 - 객체 형태로 저장
        const likesRef = ref(database, `posts/${postId}/likes`);
        await safeRemove(likesRef, '좋아요 데이터 초기화'); // 기존 likes 노드 제거
        
        // 새로운 likes 객체 저장
        const newLikes = {};
        likes.forEach((like, index) => {
          if (typeof like === 'string') {
            newLikes[`like_${index}`] = { userId: like, createdAt: new Date().toISOString() };
          } else {
            newLikes[`like_${index}`] = like;
          }
        });
        
        await update(postRef, { 
          likes: newLikes, 
          likeCount 
        });
      }
    }, { onlyOnce: true });
  } catch (error) {
    console.error('좋아요 처리 중 오류 발생:', error);
    showToast('좋아요 처리 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 댓글 버튼 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleCommentButtonClick = (event) => {
  const button = event.currentTarget;
  const postId = button.getAttribute('data-post-id');
  const commentsSection = document.getElementById(`comments-${postId}`);
  
  if (commentsSection.classList.contains('d-none')) {
    commentsSection.classList.remove('d-none');
  } else {
    commentsSection.classList.add('d-none');
  }
};

/**
 * 댓글 입력 핸들러
 * @param {Event} event - 키보드 이벤트
 */
const handleCommentInputKeydown = async (event) => {
  if (event.key !== 'Enter' || !auth.currentUser) {
    return;
  }
  
  const input = event.target;
  const content = input.value.trim();
  const postId = input.getAttribute('data-post-id');
  
  if (!content) return;
  if (!postId) {
    console.error('댓글을 작성할 게시물 ID를 찾을 수 없습니다.');
    return;
  }
  
  try {
    // 댓글 데이터 생성
    const commentData = {
      userId: auth.currentUser.uid,
      content,
      postId,
      author: {
        displayName: auth.currentUser.displayName,
        photoURL: auth.currentUser.photoURL,
        uid: auth.currentUser.uid
      },
      createdAt: new Date().toISOString()
    };
    
    // Firebase에 댓글 추가
    const commentsRef = ref(database, `posts/${postId}/comments`);
    const newCommentRef = push(commentsRef);
    commentData.id = newCommentRef.key;
    
    await set(newCommentRef, commentData);
    console.log(`댓글 데이터 저장 완료 (postId: ${postId}, commentId: ${commentData.id})`);
    
    // 댓글 수 업데이트
    const postRef = ref(database, `posts/${postId}`);
    const postSnapshot = await get(postRef);
    
    if (postSnapshot.exists()) {
      const post = postSnapshot.val();
        const comments = post.comments || {};
        const commentCount = Object.keys(comments).length;
        
        await update(postRef, { commentCount });
      console.log(`게시물 ${postId}의 댓글 수 업데이트: ${commentCount}`);
      }
    
    // Redux 스토어 업데이트
    store.dispatch(addComment(postId, commentData));
    
    // 댓글 목록 갱신
    renderCommentUpdate(postId);
    
    // 입력 필드 초기화
    input.value = '';
    
    // 권한 목록에 있는 사용자들에게 알림 전송
    sendCommentNotification(postId, commentData);
  } catch (error) {
    console.error('댓글 작성 중 오류 발생:', error);
    showToast('댓글 작성 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 댓글 목록 업데이트 함수
 * @param {string} postId - 게시물 ID
 */
const renderCommentUpdate = async (postId) => {
  try {
    if (!postId) {
      console.error('댓글 업데이트를 위한 게시물 ID가 없습니다.');
      return;
    }
    
  const commentsRef = ref(database, `posts/${postId}/comments`);
    const snapshot = await get(commentsRef);
  
    const commentsSection = document.getElementById(`comments-${postId}`);
    if (!commentsSection) {
      console.error(`#comments-${postId} 요소를 찾을 수 없습니다.`);
      return;
    }
    
    const commentsList = commentsSection.querySelector('.comments-list');
    if (!commentsList) {
      console.error(`.comments-list 요소를 찾을 수 없습니다.`);
      return;
    }
    
    if (snapshot.exists()) {
      const comments = [];
      snapshot.forEach((childSnapshot) => {
        comments.push({
          ...childSnapshot.val(),
          id: childSnapshot.key,
          postId: postId // postId를 명시적으로 설정
        });
      });
      
      // 댓글을 날짜순으로 정렬 (최신순)
      comments.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      commentsList.innerHTML = renderComments(comments);
      
      // 댓글 개수 업데이트
      const commentBtn = document.querySelector(`.comment-btn[data-post-id="${postId}"]`);
      if (commentBtn) {
        const commentCountSpan = commentBtn.querySelector('span');
        if (commentCountSpan) {
          commentCountSpan.textContent = `댓글 ${comments.length}`;
        }
      }
      
      // 댓글 삭제 버튼 이벤트 리스너 다시 설정
      const deleteButtons = commentsList.querySelectorAll('.delete-comment-btn');
      if (deleteButtons.length > 0) {
        deleteButtons.forEach(button => {
        // 기존 이벤트 리스너 제거 (중복 방지)
        button.removeEventListener('click', handleDeleteCommentClick);
        // 새 이벤트 리스너 추가
        button.addEventListener('click', handleDeleteCommentClick);
      });
      }
    } else {
      commentsList.innerHTML = renderComments([]);
      
      // 댓글이 없는 경우 댓글 개수 0으로 업데이트
      const commentBtn = document.querySelector(`.comment-btn[data-post-id="${postId}"]`);
      if (commentBtn) {
        const commentCountSpan = commentBtn.querySelector('span');
        if (commentCountSpan) {
          commentCountSpan.textContent = `댓글 0`;
        }
      }
    }
  } catch (error) {
    console.error(`댓글 업데이트 중 오류 발생 (postId: ${postId}):`, error);
  }
};

/**
 * 게시물 수정 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleEditPostClick = async (event) => {
  event.preventDefault();
  
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  const button = event.target;
  const postId = button.getAttribute('data-post-id');
  
  if (!postId) {
    console.error('수정할 게시물 ID를 찾을 수 없습니다.');
    showToast('게시물 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  // 게시물 데이터 가져오기
  const postRef = ref(database, `posts/${postId}`);
  
  try {
    const snapshot = await get(postRef);
    
    if (snapshot.exists()) {
      const post = snapshot.val();
      
      // 현재 사용자가 작성자인지 확인
      if (post.userId !== auth.currentUser.uid) {
        showToast('자신의 게시물만 수정할 수 있습니다.', 'warning');
        return;
      }
      
      // 게시물 내용을 프롬프트로 표시하고 수정
      const newContent = prompt('게시물 내용 수정:', post.content);
      
      if (newContent !== null && newContent.trim() !== '') {
        // 내용이 변경된 경우에만 업데이트
        if (newContent !== post.content) {
          try {
          // Firebase에 업데이트
            await update(postRef, {
            content: newContent,
            updatedAt: new Date().toISOString()
          });
          
          // Redux 스토어 업데이트
          store.dispatch(updatePost(postId, {
            content: newContent,
            updatedAt: new Date().toISOString()
          }));
          
          // UI 업데이트
          const postElement = document.querySelector(`.post-card[data-post-id="${postId}"]`);
          if (postElement) {
              const postTextElement = postElement.querySelector('.post-text');
              if (postTextElement) {
                postTextElement.textContent = newContent;
              }
          }
          
          showToast('게시물이 수정되었습니다.', 'success');
          } catch (error) {
            console.error('게시물 업데이트 중 오류 발생:', error);
            showToast('게시물 수정 중 오류가 발생했습니다.', 'error');
          }
        }
      }
    } else {
      console.error(`게시물 ID ${postId}에 해당하는 데이터가 없습니다`);
      showToast('수정할 게시물을 찾을 수 없습니다.', 'error');
    }
  } catch (error) {
    console.error('게시물 조회 중 오류 발생:', error);
    showToast('게시물 정보를 불러오는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 게시물 삭제 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleDeletePostClick = async (event) => {
  event.preventDefault();
  
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  const button = event.target;
  const postId = button.getAttribute('data-post-id');
  
  if (!postId) {
    console.error('삭제할 게시물 ID를 찾을 수 없습니다.');
    showToast('게시물 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  console.log(`게시물 삭제 요청: ${postId}`);
  
  // 게시물 데이터 가져오기
  const postRef = ref(database, `posts/${postId}`);
  
  try {
    const snapshot = await get(postRef);
    
    if (snapshot.exists()) {
      const post = snapshot.val();
      
      // 현재 사용자가 작성자인지 확인
      if (post.userId !== auth.currentUser.uid) {
        showToast('자신의 게시물만 삭제할 수 있습니다.', 'warning');
        return;
      }
      
      // 삭제 확인
      if (confirm('이 게시물을 삭제하시겠습니까?')) {
        try {
          console.log('관련 채팅룸 삭제 시도...');
          // 관련 채팅룸 삭제 시도
          await deleteChatRoomByPostId(postId);
          console.log('채팅룸 삭제 완료 또는 채팅룸 없음');
          
          console.log('게시물 삭제 시도...');
          // Firebase에서 게시물 삭제
        await safeRemove(postRef, '게시물 삭제');
          console.log('게시물 삭제 완료');
        
        // Redux 스토어 업데이트
        store.dispatch(deletePost(postId));
        
        // UI에서 게시물 제거
        const postElement = document.querySelector(`.post-card[data-post-id="${postId}"]`);
        if (postElement) {
          postElement.remove();
            console.log('UI에서 게시물 제거 완료');
          } else {
            console.warn('UI에서 게시물 요소를 찾을 수 없습니다');
        }
        
        showToast('게시물이 삭제되었습니다.', 'success');
        } catch (error) {
          console.error('게시물 삭제 중 오류 발생:', error);
          showToast('게시물 삭제 중 오류가 발생했습니다.', 'error');
        }
      }
    } else {
      console.error(`게시물 ID ${postId}에 해당하는 데이터가 없습니다`);
      showToast('삭제할 게시물을 찾을 수 없습니다.', 'error');
    }
  } catch (error) {
    console.error('게시물 조회 중 오류 발생:', error);
    showToast('게시물 정보를 불러오는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 게시물 ID로 관련 채팅룸 삭제
 * @param {string} postId - 게시물 ID
 */
const deleteChatRoomByPostId = async (postId) => {
  try {
    // 두 가지 가능한 경로에서 채팅룸 확인 ('chatingRooms'와 'chatRooms')
    let chatRoomRef = ref(database, `chatingRooms/${postId}`);
    let chatRoomSnapshot = await get(chatRoomRef);
    
    // 첫 번째 경로에 없으면 두 번째 경로 확인
    if (!chatRoomSnapshot.exists()) {
      chatRoomRef = ref(database, `chatRooms/${postId}`);
      chatRoomSnapshot = await get(chatRoomRef);
    }
    
    if (chatRoomSnapshot.exists()) {
      console.log(`게시물 ${postId}와 연결된 채팅룸을 삭제합니다.`);
      
      // 채팅룸 삭제
      await safeRemove(chatRoomRef, '채팅룸 삭제');
      
      // 채팅룸의 메시지도 모두 삭제
      const chatMessagesPath = chatRoomRef.toString().includes('chatingRooms') 
        ? `chatingRooms/${postId}/messages` 
        : `chatRooms/${postId}/messages`;
      const chatMessagesRef = ref(database, chatMessagesPath);
      await safeRemove(chatMessagesRef, '채팅 메시지 삭제');
      
      // 각 사용자의 해당 채팅룸 알림 설정 삭제
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      if (usersSnapshot.exists()) {
        const users = usersSnapshot.val();
        for (const userId in users) {
          const chatNotificationSettingRef = ref(database, `users/${userId}/chatNotificationSettings/${postId}`);
          await safeRemove(chatNotificationSettingRef, '채팅 알림 설정 삭제');
        }
      }
      
      console.log(`게시물 ${postId}와 연결된 채팅룸 삭제 완료`);
    } else {
      console.log(`게시물 ${postId}와 연결된 채팅룸이 없습니다.`);
    }
  } catch (error) {
    console.error(`채팅룸 삭제 중 오류 발생:`, error);
    // 채팅룸 삭제에 실패하더라도 게시물 삭제 진행
  }
};

/**
 * 댓글 삭제 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleDeleteCommentClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();
  
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  const button = event.currentTarget;
  const commentId = button.getAttribute('data-comment-id');
  const commentElement = button.closest('.comment');
  
  if (!commentElement) {
    console.error('삭제할 댓글 요소를 찾을 수 없습니다.');
    showToast('댓글 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  const commentsSection = commentElement.closest('.comments-section');
  if (!commentsSection) {
    console.error('댓글 섹션을 찾을 수 없습니다.');
    showToast('댓글 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  const postId = commentsSection.id.replace('comments-', '');
  
  if (!commentId || !postId) {
    console.error('댓글 또는 게시물 ID를 찾을 수 없습니다.');
    showToast('댓글 정보를 찾을 수 없습니다.', 'error');
    return;
  }
  
  console.log('댓글 삭제 요청:', { postId, commentId });
  
  try {
  // 댓글 데이터 가져오기
  const commentRef = ref(database, `posts/${postId}/comments/${commentId}`);
    const snapshot = await get(commentRef);
  
    if (snapshot.exists()) {
      const comment = snapshot.val();
      
      // 현재 사용자가 작성자인지 확인
      if (comment.userId !== auth.currentUser.uid) {
        showToast('자신의 댓글만 삭제할 수 있습니다.', 'warning');
        return;
      }
      
      // 삭제 확인
      if (confirm('이 댓글을 삭제하시겠습니까?')) {
        try {
          // Firebase에서 삭제
          await safeRemove(commentRef, '댓글 삭제');
          console.log(`댓글 삭제 완료: ${commentId}`);
          
          // 댓글 수 업데이트
          const postRef = ref(database, `posts/${postId}`);
          const postSnapshot = await get(postRef);
          
          if (postSnapshot.exists()) {
            const post = postSnapshot.val();
              const comments = post.comments || {};
              const commentCount = Object.keys(comments).length;
              
              await update(postRef, { commentCount });
            console.log(`게시물 ${postId}의 댓글 수 업데이트: ${commentCount}`);
            }
          
          // Redux 스토어 업데이트
          store.dispatch(deleteComment(postId, commentId));
          
          // 댓글 요소 직접 제거
          commentElement.remove();
          
          // 댓글 목록 갱신
          renderCommentUpdate(postId);
          
          showToast('댓글이 삭제되었습니다.', 'success');
        } catch (error) {
          console.error('댓글 삭제 중 오류 발생:', error);
          showToast('댓글 삭제 중 오류가 발생했습니다.', 'error');
        }
      }
    } else {
      console.error('삭제할 댓글을 찾을 수 없습니다');
      showToast('댓글 정보를 찾을 수 없습니다.', 'error');
    }
  } catch (error) {
    console.error('댓글 조회 중 오류 발생:', error);
    showToast('댓글 정보를 불러오는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 공유 버튼 클릭 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleShareButtonClick = async (event) => {
  const button = event.currentTarget;
  const postId = button.getAttribute('data-post-id');
  
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'SNS 게시물 공유',
        text: '이 게시물을 확인해보세요!',
        url: `${window.location.origin}?post=${postId}`
      });
      
      showToast('게시물을 공유했습니다.', 'success');
    } catch (error) {
      console.error('공유 중 오류 발생:', error);
    }
  } else {
    // 모바일 디바이스가 아니거나 Web Share API를 지원하지 않는 경우
    const shareUrl = `${window.location.origin}?post=${postId}`;
    
    // 클립보드에 복사
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        showToast('게시물 링크가 클립보드에 복사되었습니다.', 'success');
      })
      .catch(() => {
        prompt('아래 링크를 복사하세요:', shareUrl);
      });
  }
};

/**
 * Firebase 데이터베이스의 특정 게시물에 임베드된 패널 정보를 추가합니다.
 * @param {string} postId - 대상 게시물의 ID
 * @param {object} panelData - 저장할 패널 정보 { id, title, url }
 */
export const addEmbeddedPanelToPostData = async (postId, panelData) => {
  try {
    const embeddedPanelsRef = ref(database, `posts/${postId}/embeddedPanels`);
    const newPanelRef = push(embeddedPanelsRef); // 새 키 생성하며 추가
    await set(newPanelRef, panelData);
    console.log(`게시물 ${postId}에 임베드된 패널 정보 저장 완료:`, panelData);
  } catch (error) {
    console.error(`게시물 ${postId}에 임베드된 패널 정보 저장 실패:`, error);
    showToast('삽입된 패널 정보를 저장하는 데 실패했습니다.', 'error');
  }
};

function handleRestorePanel(event, panelData, postId, panelKey) {
  event.preventDefault();
  
  // 보안 확인 함수
  const checkPermission = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      showToast('패널을 복원하려면 로그인이 필요합니다.', 'warning');
      return false;
    }
    
    try {
      const postRef = ref(database, `posts/${postId}`);
      const postSnapshot = await get(postRef);
      
      if (postSnapshot.exists()) {
        const postData = postSnapshot.val();
        
        // 작성자와 현재 사용자 비교
        if (postData.userId === currentUser.uid) {
          return true;
        }
        
        // 이메일 포맷 변환 (Firebase 경로용)
        const userEmail = currentUser.email;
        const userEmailForPath = userEmail.replace(/\./g, ',');
        
        // 소유자 또는 관리자인지 확인
        const isOwner = postData.users && postData.users.owners && postData.users.owners[userEmailForPath];
        const isAdmin = postData.users && postData.users.admins && postData.users.admins[userEmailForPath];
        
        return isOwner || isAdmin;
      }
      return false;
    } catch (error) {
      console.error('권한 확인 오류:', error);
      return false;
    }
  };
  
  // 권한 확인 후 처리
  checkPermission().then(hasPermission => {
    if (!hasPermission) return;
    
    // 패널 타입 확인 (기본값은 web)
    const panelType = panelData.type || 'web';
    
    if (panelType === 'web') {
      // 웹 패널 처리
      
      // 이미 존재하는 draggable 패널 찾기 (URL로)
      let existingPanel = null;
      document.querySelectorAll('.draggable-panel').forEach(panel => {
        if (panel.dataset.url === panelData.url) {
          existingPanel = panel;
        }
      });
      
      if (existingPanel) {
        // 이미 존재하는 패널이 있으면 focus
        window.panelManager.focusPanel(existingPanel.id);
        removeEmbeddedPanelFromPostData(postId, panelKey);
        return;
      }
      
      // 새 패널 생성 - ID는 타임스탬프+랜덤
      const panelId = `panel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // 패널 제목
      const title = panelData.title || "웹패널";
      
      // 패널 생성
      const panel = window.panelManager.createPanel({
        id: panelId, 
        title: title, 
        url: panelData.url,
        width: panelData.width || 400,
        height: panelData.height || 300,
        left: (window.innerWidth - (panelData.width || 400)) / 2,
        top: (window.innerHeight - (panelData.height || 300)) / 2
      });
      
      // 폭과 높이가 문자열인 경우 (예: '400px') 숫자로 변환
      let width = panelData.width || 400;
      let height = panelData.height || 300;
      
      if (typeof width === 'string') {
        width = parseInt(width);
      }
      
      if (typeof height === 'string') {
        height = parseInt(height);
      }
      
      // 패널 속성 설정
      window.panelManager.updatePanelProps(panelId, {
        width: width,
        height: height,
        left: (window.innerWidth - width) / 2,
        top: (window.innerHeight - height) / 2
      });
    } else if (panelType === 'db') {
      // DB 패널 처리
      
      // DB 패널 매니저 확인
      if (!window.dbPanelManager) {
        console.error('DB 패널 매니저가 초기화되지 않았습니다.');
        showToast('DB 패널을 복원할 수 없습니다.', 'error');
        return;
      }
      
      // 새 DB 패널 생성
      const dbPanel = window.dbPanelManager.createDBPanel();
      
      // 디바이스 정보가 있으면 자동으로 해당 디바이스 선택 대기
      if (panelData.deviceId) {
        // 패널이 생성된 후 디바이스 목록을 확인하고 선택하기 위한 타이머 설정
        const selectDeviceTimer = setInterval(() => {
          if (dbPanel.deviceSelect && dbPanel.deviceSelect.options.length > 1) {
            clearInterval(selectDeviceTimer);
            
            // 디바이스 목록에서 해당 디바이스 ID 찾기
            let found = false;
            for (let i = 0; i < dbPanel.deviceSelect.options.length; i++) {
              if (dbPanel.deviceSelect.options[i].value === panelData.deviceId) {
                dbPanel.deviceSelect.selectedIndex = i;
                found = true;
                break;
              }
            }
            
            if (found) {
              // 디바이스 선택 이벤트 트리거
              const changeEvent = new Event('change');
              dbPanel.deviceSelect.dispatchEvent(changeEvent);
              
              // 패널 제목 업데이트
              const deviceName = panelData.deviceName || panelData.deviceId;
              dbPanel.setTitle(`DB 패널 - ${deviceName}`);
            }
          }
        }, 500); // 0.5초마다 확인
        
        // 10초 후에는 타이머 종료 (무한 루프 방지)
        setTimeout(() => {
          clearInterval(selectDeviceTimer);
        }, 10000);
      }
    } else {
      // 알 수 없는 패널 타입
      console.error('알 수 없는 패널 타입:', panelType);
      showToast('알 수 없는 유형의 패널입니다.', 'error');
      return;
    }
    
    // 게시물에서 임베디드 패널 제거
    removeEmbeddedPanelFromPostData(postId, panelKey);
  });
}

function removeEmbeddedPanelFromPostData(postId, panelKey) {
  // 권한 확인
  const checkPermission = async () => {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      showToast('로그인이 필요한 기능입니다.', 'warning');
      return false;
    }
    
    try {
      const postRef = ref(database, `posts/${postId}`);
      const postSnapshot = await get(postRef);
      
      if (!postSnapshot.exists()) {
        showToast('게시물을 찾을 수 없습니다.', 'error');
        return false;
      }
      
      const postData = postSnapshot.val();
      
      // 이메일 포맷 (Firebase 경로용)
      const userEmail = currentUser.email;
      const userEmailForPath = userEmail.replace(/\./g, ',');
      
      // 권한 확인
      const isAuthor = postData.userId === currentUser.uid;
      const isOwner = postData.users && postData.users.owners && postData.users.owners[userEmailForPath];
      const isAdmin = postData.users && postData.users.admins && postData.users.admins[userEmailForPath];
      
      if (!isAuthor && !isOwner && !isAdmin) {
        showToast('이 작업을 수행할 권한이 없습니다.', 'warning');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('권한 확인 오류:', error);
      showToast('권한을 확인하는 중 오류가 발생했습니다.', 'error');
      return false;
    }
  };
  
  // 권한 확인 후 처리
  checkPermission().then(hasPermission => {
    if (!hasPermission) return;
    
  const postRef = ref(database, `posts/${postId}/embeddedPanels/${panelKey}`);
  
  safeRemove(postRef, '임베디드 패널 제거')
    .then(() => {
      console.log('Embedded panel removed from post data');
      showToast('Panel restored successfully', 'success');
        
        
        // 패널이 제거된 후 UI 업데이트
        const panelElement = document.getElementById(`embedded-panel-${panelKey}`);
        if (panelElement) {
          panelElement.remove();
        }
    })
    .catch(error => {
      console.error('Error removing embedded panel:', error);
      showToast('Failed to restore panel', 'error');
      });
    });
}

function renderEmbeddedPanels(panels, postElement, postId) {
  if (!panels) return;
  
  // 사용자 권한 확인
  const userHasPermission = auth.currentUser ? true : false;
  
  // 기존 패널 컨테이너 찾기 또는 생성
  let embeddedPanelsContainer = postElement.querySelector('.embedded-panels-container');
  
  if (!embeddedPanelsContainer) {
    embeddedPanelsContainer = document.createElement('div');
    embeddedPanelsContainer.className = 'embedded-panels-container mt-3';
  } else {
    // 컨테이너가 이미 있다면 내용 초기화
    embeddedPanelsContainer.innerHTML = '';
  }
  
  Object.entries(panels).forEach(([key, panelData]) => {
    const panelContainer = document.createElement('div');
    panelContainer.className = 'embedded-panel-container mb-3 border rounded position-relative';
    panelContainer.id = `embedded-panel-${key}`;
    panelContainer.setAttribute('data-panel-title', panelData.title || 'Embedded Content');
    
    // 패널 타입 확인 (기본값은 web)
    const panelType = panelData.type || 'web';
    panelContainer.setAttribute('data-panel-type', panelType);
    
    // 웹 패널 URL 저장
    if (panelType === 'web' && panelData.url) {
      panelContainer.setAttribute('data-panel-url', panelData.url || '');
    }
    
    // DB 패널의 경우 디바이스 정보 저장
    if (panelType === 'db' && panelData.deviceId) {
      panelContainer.setAttribute('data-device-id', panelData.deviceId || '');
      panelContainer.setAttribute('data-device-name', panelData.deviceName || panelData.deviceId || '');
    }
    
    // iframe 컨테이너 생성
    const iframeContainer = document.createElement('div');
    iframeContainer.className = 'position-relative';
    
    // 패널 타입에 따라 다른 내용 표시
    if (panelType === 'web' && panelData.url) {
      // 웹 패널인 경우 iframe 생성
      const iframe = document.createElement('iframe');
      iframe.src = panelData.url;
      iframe.width = '100%';
      iframe.height = '250px';
      iframe.frameBorder = '0';
      iframe.className = 'embedded-panel-iframe';
      iframeContainer.appendChild(iframe);
    } else if (panelType === 'db') {
      // DB 패널인 경우 정보 카드 표시
      const dbInfoCard = document.createElement('div');
      dbInfoCard.className = 'db-panel-info p-3';
      
      // 헤더 추가
      const dbHeader = document.createElement('div');
      dbHeader.className = 'd-flex align-items-center mb-2';
      
      const dbIcon = document.createElement('i');
      dbIcon.className = 'bi bi-database me-2 text-primary';
      dbHeader.appendChild(dbIcon);
      
      const dbTitle = document.createElement('h6');
      dbTitle.className = 'mb-0';
      dbTitle.textContent = panelData.title || 'DB 패널';
      dbHeader.appendChild(dbTitle);
      
      dbInfoCard.appendChild(dbHeader);
      
      // 디바이스 정보 표시
      if (panelData.deviceId) {
        const deviceInfo = document.createElement('div');
        deviceInfo.className = 'small mb-2 text-muted';
        deviceInfo.innerHTML = `<strong>디바이스:</strong> ${panelData.deviceName || panelData.deviceId}`;
        dbInfoCard.appendChild(deviceInfo);
      }
      
      // 설명 추가
      const description = document.createElement('p');
      description.className = 'small text-muted mb-1';
      description.textContent = '이 DB 패널을 복원하여 디바이스 데이터를 확인하고 편집할 수 있습니다.';
      dbInfoCard.appendChild(description);
      
      // DB 패널 높이 설정
      iframeContainer.style.minHeight = '150px';
      iframeContainer.appendChild(dbInfoCard);
    } else {
      // 타입을 알 수 없는 경우 정보 메시지 표시
      const unknownPanel = document.createElement('div');
      unknownPanel.className = 'text-center p-3 text-muted';
      unknownPanel.textContent = '알 수 없는 패널 유형';
      iframeContainer.appendChild(unknownPanel);
    }
    
    // 패널 컨트롤 생성
    const panelControls = document.createElement('div');
    panelControls.className = 'position-absolute d-flex gap-2';
    panelControls.style.bottom = '10px';
    panelControls.style.right = '10px';
    panelControls.style.zIndex = '100';
    
    // 복원 버튼 생성
    const restoreButton = document.createElement('button');
    restoreButton.className = 'btn btn-sm btn-primary rounded-circle';
    restoreButton.style.width = '32px';
    restoreButton.style.height = '32px';
    restoreButton.style.padding = '0';
    restoreButton.style.display = 'flex';
    restoreButton.style.alignItems = 'center';
    restoreButton.style.justifyContent = 'center';
    restoreButton.innerHTML = '<i class="bi bi-arrow-up-right-square"></i>';
    restoreButton.title = '패널로 복원';
    restoreButton.addEventListener('click', (event) => handleRestorePanel(event, panelData, postId, key));
    
    // 사용자 권한이 없는 경우 버튼 비활성화
    if (!userHasPermission) {
      restoreButton.disabled = true;
      restoreButton.classList.add('disabled');
      restoreButton.title = '패널로 복원 (권한 필요)';
      restoreButton.style.opacity = '0.5';
      restoreButton.style.cursor = 'not-allowed';
    }
    
    // 삭제 버튼 생성
    const removeButton = document.createElement('button');
    removeButton.className = 'btn btn-sm btn-danger rounded-circle';
    removeButton.style.width = '32px';
    removeButton.style.height = '32px';
    removeButton.style.padding = '0';
    removeButton.style.display = 'flex';
    removeButton.style.alignItems = 'center';
    removeButton.style.justifyContent = 'center';
    removeButton.innerHTML = '<i class="bi bi-trash"></i>';
    removeButton.title = '게시물에서 제거';
    removeButton.addEventListener('click', (event) => {
      event.preventDefault();
      removeEmbeddedPanelFromPostData(postId, key);
    });
    
    // 사용자 권한이 없는 경우 버튼 비활성화
    if (!userHasPermission) {
      removeButton.disabled = true;
      removeButton.classList.add('disabled');
      removeButton.title = '게시물에서 제거 (권한 필요)';
      removeButton.style.opacity = '0.5';
      removeButton.style.cursor = 'not-allowed';
    }
    
    // 패널 컨트롤에 버튼들 추가
    panelControls.appendChild(restoreButton);
    panelControls.appendChild(removeButton);
    
    // iframe 컨테이너에 컨트롤 추가
    iframeContainer.appendChild(panelControls);
    
    // 패널 컨테이너에 iframe 컨테이너 추가
    panelContainer.appendChild(iframeContainer);
    
    // 임베디드 패널 컨테이너에 패널 추가
    embeddedPanelsContainer.appendChild(panelContainer);
  });
  
  // 게시물에 컨테이너 추가
  postElement.appendChild(embeddedPanelsContainer);
}

function handleEmbedPanel(event) {
  event.preventDefault();
  
  const currentPanel = window.currentDraggingPanel;
  if (!currentPanel) {
    console.error('No panel selected for embedding');
    return;
  }
  
  const currentPost = event.target.closest('.post-card');
  if (!currentPost) {
    console.error('Post element not found');
    return;
  }
  
  const postId = currentPost.dataset.postId;
  if (!postId) {
    console.error('Post ID not found');
    return;
  }
  
  const panelData = {
    title: currentPanel.querySelector('.panel-header-title').textContent,
    url: currentPanel.dataset.url,
    width: currentPanel.style.width,
    height: currentPanel.style.height
  };
  
  const postsRef = ref(database, `posts/${postId}/embeddedPanels`);
  const newPanelRef = push(postsRef);
  
  set(newPanelRef, panelData)
    .then(() => {
      console.log('Panel embedded successfully');
      showToast('Panel embedded in post', 'success');
      
      // Add panel key to data for easier management
      panelData.key = newPanelRef.key;
      
      // Close the panel
      window.panelManager.removePanel(currentPanel.id);
    })
    .catch(error => {
      console.error('Error embedding panel:', error);
      showToast('Failed to embed panel', 'error');
    });
}

/**
 * 패널 데이터를 포맷된 HTML로 변환
 * @param {string} content 포스트 내용
 * @returns {string} 패널 HTML이 포함된 내용
 */
function parseContentForPanels(content) {
  if (!content || typeof content !== 'string') return '';
  
  // [PANEL:{...}] 형식의 패널 데이터 찾기
  const panelRegex = /\[PANEL:(.*?)\]/g;
  
  // 모든 패널 데이터를 HTML로 대체
  return content.replace(panelRegex, (match, panelDataStr) => {
    try {
      const panel = JSON.parse(panelDataStr);
      return createPanelElement(panel);
    } catch (e) {
      console.error('패널 데이터 파싱 오류:', e);
      return '';
    }
  });
}

/**
 * 패널 객체로부터 임베디드 패널 HTML 생성
 * @param {Object} panel 패널 객체
 * @returns {string} 패널 HTML
 */
function createPanelElement(panel) {
  return `
    <div class="embedded-panel" data-panel-id="${panel.id}" data-panel-title="${panel.title || '웹패널'}" data-panel-url="${panel.url}">
      <div class="iframe-container">
        <iframe src="${panel.url}" class="embedded-iframe" allowfullscreen></iframe>
        <div class="overlay-controls">
          <button class="panel-restore-btn" title="웹패널로 복원하기" data-panel-id="${panel.id}" data-panel-url="${panel.url}" data-panel-title="${panel.title || '웹패널'}">
            <i class="fas fa-external-link-alt"></i>
          </button>
          <button class="panel-delete-btn" title="게시물에서 제거하기" data-panel-id="${panel.id}">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * 포스트 내용에서 패널 데이터 추출
 * @param {string} content 포스트 내용
 * @returns {Array} 패널 객체 배열
 */
function extractPanelsFromContent(content) {
  if (!content || typeof content !== 'string') return [];
  
  const panels = [];
  const panelRegex = /\[PANEL:(.*?)\]/g;
  let match;
  
  while ((match = panelRegex.exec(content)) !== null) {
    try {
      const panelData = JSON.parse(match[1]);
      panels.push(panelData);
    } catch (e) {
      console.error('패널 데이터 추출 오류:', e);
    }
  }
  
  return panels;
}

/**
 * 실시간 채팅 버튼 클릭 처리
 * @param {Event} event - 클릭 이벤트
 */
const handleChatButtonClick = async (event) => {
  event.preventDefault();
  
  const currentUser = auth.currentUser;
  if (!currentUser) {
    showToast('채팅을 시작하려면 로그인이 필요합니다.', 'warning');
    return;
  }
  
  const button = event.currentTarget;
  const postId = button.getAttribute('data-post-id');
  
  try {
    // 게시물 정보 가져오기
    const postRef = ref(database, `posts/${postId}`);
    const postSnapshot = await get(postRef);
    
    if (!postSnapshot.exists()) {
      showToast('해당 게시물을 찾을 수 없습니다.', 'error');
      return;
    }
    
    const post = postSnapshot.val();
    post.id = postId;
    
    // 채팅방 ID 생성 (게시물 ID 사용)
    const chatRoomId = postId;
    
    // 채팅방 정보 저장
    const chatRoomRef = ref(database, `chatingRooms/${chatRoomId}`);
    const chatRoomSnapshot = await get(chatRoomRef);
    
    // 채팅방이 없으면 생성
    if (!chatRoomSnapshot.exists()) {
      await set(chatRoomRef, {
        postId: postId,
        title: post.content.substring(0, 30) + '...', // 게시물 내용 일부를 제목으로 사용
        createdAt: new Date().toISOString(),
        lastMessage: null,
        lastMessageTime: null,
        participants: {
          [currentUser.uid]: {
            uid: currentUser.uid,
            displayName: currentUser.displayName || '사용자',
            photoURL: currentUser.photoURL || 'assets/default-avatar.png',
            joinedAt: new Date().toISOString()
          }
        }
      });
    } else {
      // 채팅방이 있으면 현재 사용자를 참가자로 추가
      const participants = chatRoomSnapshot.val().participants || {};
      if (!participants[currentUser.uid]) {
        await update(ref(database, `chatingRooms/${chatRoomId}/participants/${currentUser.uid}`), {
          uid: currentUser.uid,
          displayName: currentUser.displayName || '사용자',
          photoURL: currentUser.photoURL || 'assets/default-avatar.png',
          joinedAt: new Date().toISOString()
        });
      }
    }
    
    // 게시물 컨테이너 숨기기
    const postsContainer = document.getElementById('posts-container');
    if (postsContainer) {
      postsContainer.style.display = 'none';
    }
    
    // 채팅방 ID 전역 저장 (리스너 해제용)
    window.currentChatRoomId = chatRoomId;
    
    // 채팅 UI 상태 업데이트
    window.isChatUIOpen = true;
    
    // 모바일 뒤로가기 버튼 처리를 위해 더미 상태 추가
    history.pushState({ chatRoom: chatRoomId }, '', window.location.href);
    
    // 채팅 UI 컨테이너 생성 또는 표시
    let chatUIContainer = document.querySelector('.whatsapp-style-chat');
    if (!chatUIContainer) {
      chatUIContainer = document.createElement('div');
      chatUIContainer.id = 'chat-ui-container';
      chatUIContainer.className = 'whatsapp-style-chat';
      
      // 채팅 UI를 게시물 컨테이너의 부모 요소에 추가
      postsContainer.parentNode.appendChild(chatUIContainer);
    } else {
      chatUIContainer.style.display = 'flex';
    }
    
    // WhatsApp 스타일 UI 렌더링
    console.log('채팅 UI 생성 (post.js)', chatRoomId);
    chatUIContainer.innerHTML = `
      <div class="chat-ui-header">
        <button id="back-to-posts-btn" class="btn btn-link">
          <i class="bi bi-arrow-left"></i>
        </button>
        <h3 class="chat-title">${post.content.substring(0, 20)}...</h3>
        <div class="chat-header-actions">
          <button id="chat-notification-toggle-${chatRoomId}" class="btn btn-link chat-action-btn chat-notification-toggle">
            <i class="bi bi-bell"></i>
          </button>
        </div>
      </div>
      <div id="chat-messages-${chatRoomId}" class="chat-messages-area">
        <div class="chat-loading">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">로딩 중...</span>
          </div>
        </div>
      </div>
      <div class="chat-input-area">
        <div class="chat-toolbar">
          <button class="btn btn-link chat-tool-btn">
            <i class="bi bi-emoji-smile"></i>
          </button>
          <input type="file" id="chat-file-input-${chatRoomId}" class="d-none" accept="image/*,application/pdf,video/*,audio/*,text/*">
          <button id="chat-file-btn-${chatRoomId}" class="btn btn-link chat-tool-btn">
            <i class="bi bi-paperclip"></i>
          </button>
          <div class="toolbar-spacer"></div>
          <div class="dropdown chat-settings-dropdown">
            <button class="btn btn-link chat-tool-btn" id="chat-settings-btn-${chatRoomId}" data-bs-toggle="dropdown" aria-expanded="false">
              <i class="bi bi-gear"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="chat-settings-btn-${chatRoomId}">
              <li><a class="dropdown-item chat-menu-users" href="#" data-chatroom="${chatRoomId}"><i class="bi bi-people me-2"></i>사용자</a></li>
              <li><a class="dropdown-item chat-menu-clear" href="#" data-chatroom="${chatRoomId}"><i class="bi bi-trash me-2"></i>지우기</a></li>
              <li><a class="dropdown-item chat-menu-download" href="#" data-chatroom="${chatRoomId}"><i class="bi bi-download me-2"></i>다운로드</a></li>
            </ul>
          </div>
        </div>
        <div class="chat-input-wrapper">
          <textarea id="chat-input-${chatRoomId}" class="chat-input" placeholder="메시지를 입력하세요..." rows="1"></textarea>
          <button id="chat-send-btn-${chatRoomId}" class="chat-send-btn">
            <i class="bi bi-send"></i>
          </button>
        </div>
      </div>
    `;
    
    // 뒤로가기 버튼 이벤트 리스너 설정
    document.getElementById('back-to-posts-btn').addEventListener('click', () => {
      chatUIContainer.style.display = 'none';
      postsContainer.style.display = 'block';
      // 채팅 UI 상태 업데이트
      window.isChatUIOpen = false;
      window.currentChatRoomId = null;
      // 채팅 메시지 리스너 해제
      off(ref(database, `chatingRooms/${chatRoomId}/messages`));
    });
    
    // 채팅 설정 메뉴 이벤트 리스너 설정
    const chatMenuUsers = document.querySelector(`.chat-menu-users[data-chatroom="${chatRoomId}"]`);
    const chatMenuClear = document.querySelector(`.chat-menu-clear[data-chatroom="${chatRoomId}"]`);
    const chatMenuDownload = document.querySelector(`.chat-menu-download[data-chatroom="${chatRoomId}"]`);

    // 사용자 메뉴 클릭
    chatMenuUsers.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        // 채팅방 정보 가져오기
        const roomRef = ref(database, `chatingRooms/${chatRoomId}`);
        const roomSnapshot = await get(roomRef);
        
        if (roomSnapshot.exists()) {
          const roomData = roomSnapshot.val();
          const participants = roomData.participants || {};
          
          // 참가자 정보 추출
          const participantsList = [];
          for (const userId in participants) {
            const userRef = ref(database, `users/${userId}`);
            const userSnapshot = await get(userRef);
            if (userSnapshot.exists()) {
              const userData = userSnapshot.val();
              participantsList.push({
                displayName: userData.displayName || '알 수 없음',
                email: userData.email || '이메일 없음',
                photoURL: userData.photoURL || null
              });
            }
          }
          
          // 모달 생성
          const modalId = `chat-users-modal-${Date.now()}`;
          const modal = document.createElement('div');
          modal.classList.add('modal', 'fade');
          modal.id = modalId;
          modal.setAttribute('tabindex', '-1');
          modal.setAttribute('aria-labelledby', `${modalId}-label`);
          modal.setAttribute('aria-hidden', 'true');
          
          let participantsHtml = '';
          participantsList.forEach(user => {
            const userAvatar = user.photoURL ? 
              `<img src="${user.photoURL}" alt="${user.displayName}" class="avatar-sm me-2">` : 
              `<div class="avatar-placeholder me-2">${user.displayName.charAt(0)}</div>`;
            
            participantsHtml += `
              <div class="chat-user-item d-flex align-items-center mb-2">
                ${userAvatar}
                <div>
                  <div class="fw-bold">${user.displayName}</div>
                  <div class="text-muted small">${user.email}</div>
                </div>
              </div>
            `;
          });
          
          modal.innerHTML = `
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="${modalId}-label">채팅방 참가자</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                  ${participantsList.length > 0 ? participantsHtml : '<p class="text-center">참가자가 없습니다.</p>'}
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
                </div>
              </div>
            </div>
          `;
          
          document.body.appendChild(modal);
          const modalInstance = new bootstrap.Modal(modal);
          modalInstance.show();
          
          // 모달이 닫힐 때 DOM에서 제거
          const modalElement = document.getElementById(modalId);
          if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', () => {
              const modalToRemove = document.getElementById(modalId);
              if (modalToRemove) {
                modalToRemove.remove();
              }
            });
          }
        } else {
          showToast('채팅방 정보를 찾을 수 없습니다.', 'error');
        }
      } catch (error) {
        console.error('채팅방 사용자 로드 오류:', error);
        showToast('채팅방 사용자 정보를 로드하는 중 오류가 발생했습니다.', 'error');
      }
    });
    
    // 지우기 메뉴 클릭
    chatMenuClear.addEventListener('click', (e) => {
      e.preventDefault();
      
      // 확인 모달 생성
      const modalId = `chat-clear-modal-${Date.now()}`;
      const modal = document.createElement('div');
      modal.classList.add('modal', 'fade');
      modal.id = modalId;
      modal.setAttribute('tabindex', '-1');
      modal.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">채팅 내용 지우기</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p>모든 채팅 내용을 지우시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">취소</button>
              <button type="button" class="btn btn-danger" id="confirm-clear-${chatRoomId}">지우기</button>
            </div>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      const modalInstance = new bootstrap.Modal(modal);
      modalInstance.show();
      
      // 확인 버튼 이벤트
      document.getElementById(`confirm-clear-${chatRoomId}`).addEventListener('click', async () => {
        try {
          // 채팅 메시지 모두 삭제
          const messagesRef = ref(database, `chatingRooms/${chatRoomId}/messages`);
          await set(messagesRef, null);
          
          // 메시지 영역 비우기
          const chatMessagesElement = document.getElementById(`chat-messages-${chatRoomId}`);
          chatMessagesElement.innerHTML = '<div class="text-center my-4 text-muted">채팅 내용이 삭제되었습니다.</div>';
          
          modalInstance.hide();
          showToast('채팅 내용이 삭제되었습니다.', 'success');
        } catch (error) {
          console.error('채팅 삭제 오류:', error);
          showToast('채팅 삭제 중 오류가 발생했습니다.', 'error');
        }
      });
      
      // 모달이 닫히면 DOM에서 제거
      modal.addEventListener('hidden.bs.modal', function () {
        document.body.removeChild(modal);
      });
    });
    
    // 다운로드 메뉴 클릭
    chatMenuDownload.addEventListener('click', async (e) => {
      e.preventDefault();
      
      try {
        // 채팅방 정보 가져오기
        const messagesRef = ref(database, `chatingRooms/${chatRoomId}/messages`);
        const messagesSnapshot = await get(messagesRef);
        
        if (messagesSnapshot.exists()) {
          const messages = messagesSnapshot.val();
          let chatContent = '=== 채팅 내용 ===\n\n';
          
          // 채팅 메시지 포맷팅
          for (const messageId in messages) {
            const message = messages[messageId];
            const timestamp = new Date(message.timestamp).toLocaleString();
            const userName = message.user ? message.user.displayName : '알 수 없음';
            
            chatContent += `[${timestamp}] ${userName}: ${message.text}\n`;
          }
          
          // 파일 다운로드
          const blob = new Blob([chatContent], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `채팅내용_${new Date().toISOString().slice(0, 10)}.txt`;
          document.body.appendChild(a);
          a.click();
          
          // 정리
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
          
          showToast('채팅 내용이 다운로드되었습니다.', 'success');
        } else {
          showToast('다운로드할 채팅 내용이 없습니다.', 'warning');
        }
      } catch (error) {
        console.error('채팅 다운로드 오류:', error);
        showToast('채팅 내용 다운로드 중 오류가 발생했습니다.', 'error');
      }
    });
    
    // 채팅 메시지 불러오기 및 이벤트 리스너 설정
    setupChatListenersForFullUI(chatRoomId);
    
    showToast('채팅방에 입장했습니다.', 'success');
  } catch (error) {
    console.error('채팅방 생성 오류:', error);
    showToast('채팅방을 생성하는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 전체 UI 채팅용 리스너 설정
 * @param {string} chatRoomId - 채팅방 ID
 */
export const setupChatListenersForFullUI = (chatRoomId) => {
  const sendButton = document.getElementById(`chat-send-btn-${chatRoomId}`);
  
  if (sendButton) {
    sendButton.addEventListener('click', () => sendMessageFullUI(chatRoomId));
  } else {
    console.error(`전송 버튼(#chat-send-btn-${chatRoomId})을 찾을 수 없습니다.`);
  }
  
  // 파일 첨부 버튼 이벤트 리스너 설정
  const fileButton = document.getElementById(`chat-file-btn-${chatRoomId}`);
  const fileInput = document.getElementById(`chat-file-input-${chatRoomId}`);
  
  if (fileButton && fileInput) {
    fileButton.addEventListener('click', () => {
      fileInput.click();
    });
    
    fileInput.addEventListener('change', (e) => {
      if (e.target.files && e.target.files.length > 0) {
        // 파일이 선택되면 UI에 표시
        const file = e.target.files[0];
        showSelectedFileUI(file, chatRoomId);
      }
    });
  } else {
    console.error(`파일 업로드 버튼 또는 입력 필드를 찾을 수 없습니다.`);
  }
  
  // 알림 토글 버튼 처리
  const notificationToggle = document.getElementById(`chat-notification-toggle-${chatRoomId}`);
  if (notificationToggle) {
    // 현재 알림 설정 확인하고 UI 업데이트
    checkChatNotificationSetting(chatRoomId).then(isEnabled => {
      updateNotificationToggleButton(notificationToggle, isEnabled);
    });
    
    // 알림 토글 버튼 클릭 이벤트
    notificationToggle.addEventListener('click', async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          showToast('알림 설정을 변경하려면 로그인이 필요합니다.', 'warning');
          return;
        }
        
        // 현재 설정 가져오기
        const isCurrentlyEnabled = await checkChatNotificationSetting(chatRoomId);
        
        // 설정 토글
        await setChatNotificationSetting(chatRoomId, !isCurrentlyEnabled);
        
        // UI 업데이트
        updateNotificationToggleButton(notificationToggle, !isCurrentlyEnabled);
        
        // 토스트 메시지 표시
        const statusText = !isCurrentlyEnabled ? '활성화' : '비활성화';
        showToast(`채팅방 알림이 ${statusText}되었습니다.`, 'success');
      } catch (error) {
        console.error('알림 설정 변경 중 오류:', error);
        showToast('알림 설정을 변경하는 데 실패했습니다.', 'error');
      }
    });
  }
  
  const inputField = document.getElementById(`chat-input-${chatRoomId}`);
  if (inputField) {
    inputField.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault(); // Enter 키의 기본 동작 방지
        sendMessageFullUI(chatRoomId);
      }
      
      // ESC 키를 누르면 채팅창 닫기
      if (e.key === 'Escape' && window.handleBackButtonPress) {
        window.handleBackButtonPress();
      }
      
      // 높이 자동 조절
      setTimeout(() => {
        inputField.style.height = 'auto';
        inputField.style.height = (inputField.scrollHeight) + 'px';
      }, 0);
    });
    
    // 입력 필드 리사이즈 이벤트
    inputField.addEventListener('input', () => {
      inputField.style.height = 'auto';
      inputField.style.height = (inputField.scrollHeight) + 'px';
    });
    
    // 포커스 설정
    setTimeout(() => {
      inputField.focus();
    }, 300);
  } else {
    console.error(`입력 필드(#chat-input-${chatRoomId})를 찾을 수 없습니다.`);
  }
  
  // 메시지 영역에 클릭 이벤트 위임 - 수정/삭제 버튼 처리
  const messagesContainer = document.querySelector('.chat-messages-area');
  if (messagesContainer) {
    messagesContainer.addEventListener('click', async (e) => {
      // 수정 버튼 클릭
      if (e.target.closest('.btn-message-edit')) {
        const button = e.target.closest('.btn-message-edit');
        const messageId = button.getAttribute('data-message-id');
        const messageElement = button.closest('.chat-message');
        const messageTextElement = messageElement.querySelector('.message-text');
        const currentText = messageTextElement.innerText;
        
        // 인라인 에디터 생성
        const newText = prompt('메시지를 수정하세요:', currentText);
        if (newText !== null && newText.trim() !== '' && newText !== currentText) {
          await editChatMessage(chatRoomId, messageId, newText);
        }
      }
      
      // 삭제 버튼 클릭
      if (e.target.closest('.btn-message-delete')) {
        const button = e.target.closest('.btn-message-delete');
        const messageId = button.getAttribute('data-message-id');
        
        if (confirm('메시지를 삭제하시겠습니까?')) {
          await deleteChatMessage(chatRoomId, messageId);
        }
      }
    });
  }
  
  // 채팅 메시지 실시간 업데이트
  const messagesRef = ref(database, `chatingRooms/${chatRoomId}/messages`);
  onValue(messagesRef, (snapshot) => {
    updateChatMessagesFullUI(snapshot, chatRoomId);
  });
  
  // 채팅방 정보 가져오기
  const chatRoomRef = ref(database, `chatingRooms/${chatRoomId}`);
  onValue(chatRoomRef, (snapshot) => {
    if (snapshot.exists()) {
      // 채팅방 제목 업데이트
      const chatRoom = snapshot.val();
      const chatTitle = document.querySelector('.chat-title');
      if (chatTitle && chatRoom.title) {
        chatTitle.textContent = chatRoom.title;
      }
    }
  }, { onlyOnce: true });
};

/**
 * 채팅 메시지 UI 업데이트 함수
 * @param {object} snapshot - Firebase 데이터 스냅샷
 * @param {string} chatRoomId - 채팅방 ID
 */
const updateChatMessagesFullUI = (snapshot, chatRoomId) => {
  const currentUser = auth.currentUser;
  const messages = snapshot.val() || {};
  const messagesContainer = document.getElementById(`chat-messages-${chatRoomId}`);
  
  if (!messagesContainer) {
    console.error(`메시지 컨테이너(#chat-messages-${chatRoomId})를 찾을 수 없습니다.`);
    return;
  }
  
  // 로딩 표시 제거
  const loadingEl = messagesContainer.querySelector('.chat-loading');
  if (loadingEl) {
    loadingEl.remove();
  }
  
  // 메시지가 없는 경우
  if (Object.keys(messages).length === 0) {
    messagesContainer.innerHTML = `
      <div class="no-messages">
        <p>아직 메시지가 없습니다. 첫 메시지를 작성해보세요!</p>
      </div>
    `;
    return;
  }
  
  // 메시지 시간별 그룹화를 위한 준비
  const messagesByDate = {};
  const messagesList = Object.entries(messages).map(([key, msg]) => ({
    ...msg,
    id: key,
    chatRoomId: chatRoomId // 수정/삭제용 채팅방 ID 추가
  })).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  
  // 날짜별로 메시지 그룹화
  messagesList.forEach(message => {
    const messageDate = new Date(message.timestamp).toLocaleDateString();
    if (!messagesByDate[messageDate]) {
      messagesByDate[messageDate] = [];
    }
    messagesByDate[messageDate].push(message);
  });
  
  // 메시지 컨테이너에 날짜별로 그룹화된 메시지 표시
  let html = '<div class="chat-message-container">';
  
  Object.keys(messagesByDate).forEach(date => {
    // 날짜 구분선 추가
    html += `
      <div class="chat-date-divider">
        <span class="chat-date-text">${formatChatDate(date)}</span>
      </div>
      <div class="chat-message-group">
    `;
    
    let currentSenderId = null;
    
    // 같은 날짜의 메시지들 렌더링
    messagesByDate[date].forEach(message => {
      // 사용자가 바뀌면 새로운 메시지 그룹 시작
      const senderChanged = currentSenderId !== null && currentSenderId !== message.userId;
      if (senderChanged) {
        html += `</div><div class="chat-message-group">`;
      }
      currentSenderId = message.userId;
      
      html += renderChatMessage(message, currentUser.uid);
    });
    
    html += `</div>`;
  });
  
  html += '</div>';
  
  messagesContainer.innerHTML = html;
  
  // 스크롤을 최신 메시지로 이동
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
};

/**
 * 채팅 날짜 포맷팅
 * @param {string} dateString - 날짜 문자열
 * @returns {string} 포맷팅된 날짜 문자열
 */
const formatChatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  // 오늘, 어제, 또는 날짜 그대로 표시
  if (date.toDateString() === today.toDateString()) {
    return '오늘';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return '어제';
  } else {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
};

/**
 * 전체화면 UI용 메시지 전송 함수
 * @param {string} chatRoomId - 채팅방 ID
 */
const sendMessageFullUI = async (chatRoomId) => {
  const currentUser = auth.currentUser;
  const inputField = document.getElementById(`chat-input-${chatRoomId}`);
  const fileInput = document.getElementById(`chat-file-input-${chatRoomId}`);
  
  if (!inputField) {
    console.error(`메시지 전송 실패: 입력 필드(#chat-input-${chatRoomId})를 찾을 수 없습니다.`);
    return;
  }
  
  const messageText = inputField.value.trim();
  const hasFile = fileInput && fileInput.files && fileInput.files.length > 0;
  
  // 텍스트가 없고 파일도 없으면 전송하지 않음
  if (!messageText && !hasFile) return;
  
  try {
    showLoading(true, `chat-messages-${chatRoomId}`, '전송 중...');
    
    // 파일 업로드 처리
    let fileData = null;
    
    if (hasFile) {
      const file = fileInput.files[0];
      fileData = await uploadChatFile(file, chatRoomId);
    }
    
    // 새 메시지 추가
    const messagesRef = ref(database, `chatingRooms/${chatRoomId}/messages`);
    const newMessageRef = push(messagesRef);
    
    const messageData = {
      text: messageText,
      timestamp: new Date().toISOString(),
      userId: currentUser.uid,
      user: {
        displayName: currentUser.displayName || '사용자',
        photoURL: currentUser.photoURL || 'assets/default-avatar.png'
      }
    };
    
    // 파일 정보가 있으면 메시지 데이터에 추가
    if (fileData) {
      messageData.file = fileData;
    }
    
    await set(newMessageRef, messageData);
    
    // 채팅방 정보 업데이트
    const lastMessageText = messageText || (fileData ? `[${fileData.type === 'image' ? '이미지' : '파일'} 첨부]` : '');
    
    await update(ref(database, `chatingRooms/${chatRoomId}`), {
      lastMessage: lastMessageText,
      lastMessageTime: messageData.timestamp,
      lastSender: currentUser.uid
    });
    
    // FCM 알림 전송을 위한 데이터 준비
    sendChatNotification(chatRoomId, messageData);
    
    // 입력창 비우기 및 파일 선택 초기화
    inputField.value = '';
    if (fileInput) fileInput.value = '';
    
    // 파일 미리보기 UI 제거
    const filePreview = document.querySelector('.chat-file-preview');
    if (filePreview) {
      filePreview.remove();
    }
    
  } catch (error) {
    console.error('메시지 전송 오류:', error);
    showToast('메시지를 전송하는 중 오류가 발생했습니다.', 'error');
  } finally {
    showLoading(false, `chat-messages-${chatRoomId}`);
  }
};

/**
 * 채팅 파일 업로드 함수
 * @param {File} file - 업로드할 파일
 * @param {string} chatRoomId - 채팅방 ID
 * @returns {Promise<Object>} 업로드된 파일 정보
 */
const uploadChatFile = async (file, chatRoomId) => {
  if (!file) return null;
  
  try {
    // 파일 크기 제한 (10MB)
    const maxSizeInBytes = 10 * 1024 * 1024;
    
    if (file.size > maxSizeInBytes) {
      throw new Error(`파일 크기는 최대 10MB까지 가능합니다. (현재: ${formatFileSize(file.size)})`);
    }
    
    // 파일명 생성 (타임스탬프 + 원본 파일명)
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const uploadPath = `chats/${chatRoomId}/${fileName}`;
    
    // Firebase Storage에 업로드
    const fileUploadRef = storageRef(storage, uploadPath);
    
    // 업로드 상태 메시지 표시
    showToast('파일 업로드 중...', 'info');
    
    // 파일 업로드
    await uploadBytes(fileUploadRef, file);
    
    // 업로드된 파일의 다운로드 URL 가져오기
    const downloadURL = await getDownloadURL(fileUploadRef);
    
    // 파일 유형 결정
    let fileType = 'file';
    
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
    } else if (file.type.startsWith('audio/')) {
      fileType = 'audio';
    } else if (file.type === 'application/pdf') {
      fileType = 'pdf';
    } else if (file.type.startsWith('text/')) {
      fileType = 'text';
    }
    
    // 파일 정보 객체 반환
    return {
      name: file.name,
      type: fileType,
      mimeType: file.type,
      size: file.size,
      url: downloadURL,
      path: uploadPath,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('파일 업로드 중 오류:', error);
    showToast(`파일 업로드 실패: ${error.message}`, 'error');
    throw error;
  }
};

/**
 * FCM 알림 전송 함수
 * @param {string} chatRoomId - 채팅방 ID
 * @param {Object} messageData - 메시지 데이터
 */
const sendChatNotification = async (chatRoomId, messageData) => {
  try {
    // 채팅방의 참가자 정보 가져오기
    const chatRoomRef = ref(database, `chatingRooms/${chatRoomId}`);
    const chatRoomSnapshot = await get(chatRoomRef);
    
    if (!chatRoomSnapshot.exists()) return;
    
    const chatRoom = chatRoomSnapshot.val();
    const participants = chatRoom.participants || {};
    
    // 참가자가 없으면 리턴
    if (!participants || Object.keys(participants).length === 0) {
      console.log('알림 전송: 참가자가 없음');
      return;
    }
    
    // 채팅방 정보 가져오기 (제목 표시용)
    let chatRoomName = chatRoom.title || '새 채팅';
    // chatRoomName += '\n';

    // 채팅방 정보 가져오기 (제목 표시용)
    console.log(chatRoom);
    // alert( chatRoom.name );

    // Cloud Functions 호출을 위한 엔드포인트
    const sendFcmEndpoint = 'https://us-central1-kdindoor-1ac7f.cloudfunctions.net/sendChatNotification';

    // 현재 사용자를 제외한 참가자들에게 알림 전송
    for (const userId of Object.keys(participants)) {
      if (userId === messageData.userId) continue; // 메시지 발신자는 제외
      
      try {
        // 사용자의 채팅방 알림 설정 확인
        const notificationSettingRef = ref(database, `users/${userId}/chatNotificationSettings/${chatRoomId}`);
        const notificationSettingSnapshot = await get(notificationSettingRef);
        
        // 알림 설정이 비활성화되어 있으면 스킵 (설정이 없으면 기본값은 true로 간주)
        if (notificationSettingSnapshot.exists() && notificationSettingSnapshot.val() === false) {
          console.log(`${userId}에게 알림 전송: 알림 설정 비활성화됨`);
          continue;
        }
        
        // 1. 참가자의 FCM 토큰 가져오기
        const userTokensRef = ref(database, `users/${userId}/fcmTokens`);
        const userTokensSnapshot = await get(userTokensRef);
        
        if (!userTokensSnapshot.exists()) {
          console.log(`${userId} 사용자의 FCM 토큰이 없습니다.`);
          continue;
        }
        
        const tokens = userTokensSnapshot.val();
        
        // 토큰이 없으면 스킵
        if (!tokens || Object.keys(tokens).length === 0) {
          console.log(`${userId}에게 알림 전송: FCM 토큰 없음`);
          continue;
        }
        
        // 2. 각 토큰에 대한 알림 처리
        const tokenList = Object.values(tokens).filter(token => token && typeof token === 'string');

        if (tokenList.length === 0) {
          console.log(`${userId}에게 알림 전송: 유효한 FCM 토큰 없음`);
          continue;
        }
        
        // 3. 데이터베이스에 알림 정보 저장 (기존 로직 유지)
        const notificationRef = ref(database, `users/${userId}/notifications/${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
        const notificationData = {
          type: 'chat',
          title: chatRoomName,
          body: `${messageData.user.displayName}: ${messageData.text}`,
          chatRoomId: chatRoomId,
          senderId: messageData.userId,
          senderName: messageData.user.displayName,
          timestamp: new Date().toISOString(),
          read: false
        };
        
        await set(notificationRef, notificationData);
        console.log(`${userId}에게 알림 데이터 저장 완료`);
        
        console.log("authToken", await auth.currentUser.getIdToken());
        console.log("devToken", tokenList);

        // 4. Cloud Functions를 통해 FCM 전송 (HTTP 호출)
        try {
          console.log('FCM 알림 전송 시작:', tokenList.length, '개의 토큰에 전송 시도');
          const response = await fetch(sendFcmEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
            },
            mode: 'cors',
            body: JSON.stringify({
              tokens: tokenList,
              notification: {
                title: chatRoomName,
                body: `${messageData.user.displayName}: ${messageData.text.substring(0, 100)}${messageData.text.length > 100 ? '...' : ''}`,
              },
              data: {
                chatRoomId: chatRoomId,
                messageId: messageData.id || '',
                senderId: messageData.userId,
                type: 'chat_message',
                clickAction: 'OPEN_CHAT_ROOM'
              }
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('FCM 전송 성공:', result);
          } else {
            const errorText = await response.text();
            console.error('FCM 전송 실패:', response.status, errorText);
            
            // 오류가 404인 경우 엔드포인트 확인 안내
            if (response.status === 404) {
              console.error('404 오류: Cloud Functions 엔드포인트를 확인하세요. 이 함수가 배포되었는지 확인해주세요.');
            }
          }
        } catch (error) {
          console.error('FCM 전송 중 오류 발생:', error.message);
        }
      } catch (userError) {
        console.error(`${userId}에게 알림 전송 중 오류:`, userError);
      }
    }
  } catch (error) {
    console.error('알림 전송 오류:', error);
  }
};

/**
 * 메시지 수정 함수
 * @param {string} chatRoomId - 채팅방 ID
 * @param {string} messageId - 메시지 ID
 * @param {string} newText - 새로운 메시지 내용
 */
const editChatMessage = async (chatRoomId, messageId, newText) => {
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  try {
    const messageRef = ref(database, `chatingRooms/${chatRoomId}/messages/${messageId}`);
    const messageSnapshot = await get(messageRef);
    
    if (!messageSnapshot.exists()) {
      showToast('메시지를 찾을 수 없습니다.', 'error');
      return;
    }
    
    const message = messageSnapshot.val();
    
    // 본인 메시지인지 확인
    if (message.userId !== auth.currentUser.uid) {
      showToast('자신의 메시지만 수정할 수 있습니다.', 'warning');
      return;
    }
    
    // 메시지 내용과 수정 시간 업데이트
    await update(messageRef, {
      text: newText,
      edited: true,
      editedAt: new Date().toISOString()
    });
    
    showToast('메시지가 수정되었습니다.', 'success');
  } catch (error) {
    console.error('메시지 수정 오류:', error);
    showToast('메시지 수정 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 메시지 삭제 함수
 * @param {string} chatRoomId - 채팅방 ID
 * @param {string} messageId - 메시지 ID
 */
const deleteChatMessage = async (chatRoomId, messageId) => {
  if (!auth.currentUser) {
    showToast('로그인이 필요한 기능입니다.', 'warning');
    return;
  }
  
  try {
    const messageRef = ref(database, `chatingRooms/${chatRoomId}/messages/${messageId}`);
    const messageSnapshot = await get(messageRef);
    
    if (!messageSnapshot.exists()) {
      showToast('메시지를 찾을 수 없습니다.', 'error');
      return;
    }
    
    const message = messageSnapshot.val();
    
    // 본인 메시지인지 확인
    if (message.userId !== auth.currentUser.uid) {
      showToast('자신의 메시지만 삭제할 수 있습니다.', 'warning');
      return;
    }
    
    // 메시지 삭제 대신 내용만 변경 (대화의 흐름을 유지하기 위해)
    await update(messageRef, {
      text: '이 메시지는 삭제되었습니다.',
      deleted: true,
      deletedAt: new Date().toISOString()
    });
    
    showToast('메시지가 삭제되었습니다.', 'success');
  } catch (error) {
    console.error('메시지 삭제 오류:', error);
    showToast('메시지 삭제 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 메시지 시간 포맷팅
 * @param {string} timestamp - ISO 형식 타임스탬프
 * @returns {string} 포맷팅된 시간 문자열
 */
const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 메시지를 UI에 추가하는 함수
function renderChatMessage(message, currentUserId) {
  const messageClass = message.userId === currentUserId ? 'message-outgoing' : 'message-incoming';
  const formattedTime = formatMessageTime(message.timestamp);
  
  // 사용자 정보 직접 가져오기
  const userInfo = message.user || {};
  const displayName = userInfo.displayName || '알 수 없는 사용자';
  
  // 수정됨/삭제됨 표시
  let messageStatus = '';
  let bubbleClass = '';
  
  if (message.deleted) {
    bubbleClass = 'deleted';
    messageStatus = '';
  } else if (message.edited) {
    messageStatus = ' <small class="message-edited">(수정됨)</small>';
  }
  
  // 메시지 수정/삭제 버튼 (자신의 메시지에만 표시, 삭제된 메시지에는 표시 안함)
  const messageActions = (message.userId === currentUserId && !message.deleted) ? 
    `<div class="message-actions">
      <button class="btn-message-edit" data-message-id="${message.id || ''}" data-chat-room-id="${message.chatRoomId || ''}" title="수정">
        <i class="bi bi-pencil"></i>
      </button>
      <button class="btn-message-delete" data-message-id="${message.id || ''}" data-chat-room-id="${message.chatRoomId || ''}" title="삭제">
        <i class="bi bi-trash"></i>
      </button>
    </div>` : '';
  
  // 파일 첨부 부분 처리
  let fileContent = '';
  if (message.file && !message.deleted) {
    const file = message.file;
    const fileSize = formatFileSize(file.size);
    
    // 파일 유형에 따라 다르게 표시
    if (file.type === 'image') {
      // 이미지 파일 (섬네일로 표시하고 클릭 시 원본 이미지 표시)
      fileContent = `
        <div class="message-file image-file">
          <img src="${file.url}" alt="${file.name}" class="message-image modal-trigger-image" data-original-url="${file.url}">
        </div>`;
    } else if (file.type === 'video') {
      // 비디오 파일
      fileContent = `
        <div class="message-file video-file">
          <video class="message-video" controls>
            <source src="${file.url}" type="${file.mimeType}">
            브라우저가 비디오를 지원하지 않습니다.
          </video>
          <a href="${file.url}" target="_blank" class="file-download-link">
            <div class="file-preview">
              <i class="bi bi-file-earmark-play file-icon"></i>
            </div>
            <div class="file-details">
              <div class="file-name">${file.name}</div>
              <div class="file-size">${fileSize}</div>
            </div>
          </a>
        </div>`;
    } else if (file.type === 'audio') {
      // 오디오 파일
      fileContent = `
        <div class="message-file audio-file">
          <audio class="message-audio" controls>
            <source src="${file.url}" type="${file.mimeType}">
            브라우저가 오디오를 지원하지 않습니다.
          </audio>
          <a href="${file.url}" target="_blank" class="file-download-link">
            <div class="file-preview">
              <i class="bi bi-file-earmark-music file-icon"></i>
            </div>
            <div class="file-details">
              <div class="file-name">${file.name}</div>
              <div class="file-size">${fileSize}</div>
            </div>
          </a>
        </div>`;
    } else if (file.type === 'pdf') {
      // PDF 파일
      fileContent = `
        <div class="message-file pdf-file">
          <a href="${file.url}" target="_blank" class="file-download-link">
            <div class="file-preview">
              <i class="bi bi-file-earmark-pdf file-icon"></i>
            </div>
            <div class="file-details">
              <div class="file-name">${file.name}</div>
              <div class="file-size">${fileSize}</div>
            </div>
          </a>
        </div>`;
    } else {
      // 기타 파일
      fileContent = `
        <div class="message-file">
          <a href="${file.url}" target="_blank" class="file-download-link">
            <div class="file-preview">
              <i class="bi bi-file-earmark file-icon"></i>
            </div>
            <div class="file-details">
              <div class="file-name">${file.name}</div>
              <div class="file-size">${fileSize}</div>
            </div>
          </a>
        </div>`;
    }
  }
  
  // 메시지 내용 및 파일 합치기
  const messageContent = message.text ? `<div class="message-text">${message.text}${messageStatus}</div>` : '';
  
  // 메시지 HTML 생성
  if (messageClass === 'message-outgoing') {
    return `<div class="chat-message ${messageClass}" data-message-id="${message.id || ''}"><div class="message-content"><div class="message-bubble ${bubbleClass}">${messageContent}${fileContent}${messageActions}</div><div class="message-time">${formattedTime}</div></div><div class="message-avatar" data-user-id="${message.userId}">${displayName.charAt(0)}</div></div>`;
  } else {
    return `<div class="chat-message ${messageClass}" data-message-id="${message.id || ''}"><div class="message-avatar" data-user-id="${message.userId}">${displayName.charAt(0)}</div><div class="message-content"><div class="message-bubble ${bubbleClass}">${messageContent}${fileContent}</div><div class="message-time">${formattedTime}</div></div></div>`;
  }
}

export { handleChatButtonClick, sendMessageFullUI, editChatMessage, deleteChatMessage }; 

/**
 * 채팅방 알림 설정 상태 확인
 * @param {string} chatRoomId - 채팅방 ID
 * @returns {Promise<boolean>} 알림 활성화 여부
 */
const checkChatNotificationSetting = async (chatRoomId) => {
  const currentUser = auth.currentUser;
  if (!currentUser) return true; // 기본값은 활성화
  
  try {
    const settingRef = ref(database, `users/${currentUser.uid}/chatNotificationSettings/${chatRoomId}`);
    const snapshot = await get(settingRef);
    
    if (snapshot.exists()) {
      return snapshot.val() === true;
    } else {
      // 설정이 없으면 기본값은 활성화 (true)
      await set(settingRef, true);
      return true;
    }
  } catch (error) {
    console.error('알림 설정 확인 중 오류:', error);
    return true; // 오류 시 기본값 반환
  }
};

/**
 * 채팅방 알림 설정 변경
 * @param {string} chatRoomId - 채팅방 ID
 * @param {boolean} isEnabled - 활성화 여부
 * @returns {Promise<void>}
 */
const setChatNotificationSetting = async (chatRoomId, isEnabled) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('사용자 인증이 필요합니다.');
  
  const settingRef = ref(database, `users/${currentUser.uid}/chatNotificationSettings/${chatRoomId}`);
  await set(settingRef, isEnabled);
};

/**
 * 알림 토글 버튼 UI 업데이트
 * @param {HTMLElement} button - 버튼 요소
 * @param {boolean} isEnabled - 활성화 여부
 */
const updateNotificationToggleButton = (button, isEnabled) => {
  const iconElement = button.querySelector('i');
  if (iconElement) {
    // 아이콘 클래스 변경
    if (isEnabled) {
      iconElement.className = 'bi bi-bell';
      button.setAttribute('title', '알림 비활성화');
    } else {
      iconElement.className = 'bi bi-bell-slash';
      button.setAttribute('title', '알림 활성화');
    }
  }
};

/**
 * 권한 뱃지 클릭 이벤트 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleRoleBadgeClick = (event) => {
  event.preventDefault();
  const badge = event.currentTarget;
  const postId = badge.getAttribute('data-post-id');
  const currentRole = badge.getAttribute('data-role');

  // 기존 모달이 있으면 제거
  const existingModal = document.getElementById('permissionModal');
  if (existingModal) {
    existingModal.remove();
  }

  // 현재 로그인한 사용자 확인
  const currentUser = auth.currentUser;
  if (!currentUser) {
    showToast('권한 관리를 위해서는 로그인이 필요합니다.', 'warning');
    return;
  }

  // 다크모드 감지
  const isDarkMode = document.body.classList.contains('dark-mode') || 
                     (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // 다크모드 클래스
  const darkModeClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  const darkModeCardClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  const darkModeInputClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  const darkModeHeaderClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  const darkModeFooterClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';

  // 모달 HTML 생성
  const modalHTML = `
    <div class="modal fade" id="permissionModal" tabindex="-1" aria-labelledby="permissionModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content ${darkModeCardClasses}">
          <div class="modal-header ${darkModeHeaderClasses}">
            <h5 class="modal-title" id="permissionModalLabel">
              <i class="bi bi-shield-lock me-2"></i>권한 관리
            </h5>
            <div class="ms-auto d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-outline-primary me-2" id="start-permissions-tour-btn" title="권한 관리 투어 시작">
                <i class="bi bi-info-circle"></i> 투어
              </button>
              <button type="button" class="btn-close ${isDarkMode ? 'btn-close-white' : ''}" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>
          
          <div class="modal-body ${darkModeClasses}">
            <!-- 현재 사용자 권한 정보 -->
            <div class="current-permission p-3 border-bottom mb-3">
              <h6 class="mb-3">
                <i class="bi bi-person-badge me-2"></i>현재 사용자 권한
              </h6>
              <div class="d-flex align-items-center p-2 rounded border ${isDarkMode ? 'border-secondary' : ''}">
                <div class="rounded-circle me-3 p-2 bg-secondary text-white">
                  <i class="bi bi-person-fill"></i>
                </div>
                <div>
                  <div class="fw-bold">${currentUser.email}</div>
                  <div class="d-flex align-items-center">
                    <span class="badge ${getBadgeClassForRole(currentRole)} me-2">${currentRole}</span>
                    <span class="${isDarkMode ? 'text-light' : 'text-muted'} small">${getRoleDescription(currentRole)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 사용자 권한 추가 -->
            <div class="mb-4">
              <h6 class="mb-3">
                <i class="bi bi-person-plus-fill me-2"></i>사용자 권한 추가
              </h6>
              <div class="card ${darkModeCardClasses}">
                <div class="card-body">
                  <div class="mb-3">
                    <label for="permission-email-select" class="form-label">사용자 선택</label>
                    <div class="user-select-container">
                      <div class="input-group mb-2">
                        <span class="input-group-text ${darkModeInputClasses}">
                          <i class="bi bi-search"></i>
                        </span>
                        <input type="text" class="form-control ${darkModeInputClasses}" id="user-search" 
                          placeholder="사용자 검색..." autocomplete="off">
                      </div>
                      <select class="form-select ${darkModeInputClasses}" id="permission-email-select" size="4">
                        <option value="">-- 사용자 선택 --</option>
                        <option value="" disabled>사용자 목록 로딩 중...</option>
                      </select>
                    </div>
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">권한 선택</label>
                    <div class="role-select-container mb-3">
                      <div class="d-flex flex-wrap gap-2">
                        <div class="form-check">
                          <input class="form-check-input ${darkModeInputClasses}" type="radio" name="role-option" id="role-owner" value="owner" checked>
                          <label class="form-check-label" for="role-owner">
                            Owner
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input ${darkModeInputClasses}" type="radio" name="role-option" id="role-manager" value="manager">
                          <label class="form-check-label" for="role-manager">
                            Manager
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input ${darkModeInputClasses}" type="radio" name="role-option" id="role-viewer" value="viewer">
                          <label class="form-check-label" for="role-viewer">
                            Viewer
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input ${darkModeInputClasses}" type="radio" name="role-option" id="role-admin" value="admin">
                          <label class="form-check-label" for="role-admin">
                            Admin
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div class="d-grid">
                      <button class="btn btn-primary" id="add-permission-btn">
                        <i class="bi bi-plus-circle me-2"></i>권한 추가하기
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              
            <!-- 권한 목록 -->
            <div>
              <h6 class="mb-3">
                <i class="bi bi-people-fill me-2"></i>권한 목록
              </h6>
              <div class="card ${darkModeCardClasses}">
                <div class="card-body">
                  <div class="permissions-list" id="permissions-list">
                    <div class="text-center p-4">
                      <div class="spinner-border" role="status">
                        <span class="visually-hidden">로딩 중...</span>
                      </div>
                      <p class="${isDarkMode ? 'text-light' : 'text-muted'} mt-3">권한 목록을 불러오는 중입니다...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer ${darkModeFooterClasses}">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bi bi-x-circle me-2"></i>닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // 모달을 body에 추가
  document.body.insertAdjacentHTML('beforeend', modalHTML);

  // 모달 표시
  const modalElement = document.getElementById('permissionModal');
  const modal = new bootstrap.Modal(modalElement);
  modal.show();

  // 권한 목록과 인증된 사용자 목록 불러오기
  Promise.all([loadAuthenticatedUsers(), loadPermissionsList(postId)]);

  // 다크모드 시 드롭다운 옵션 스타일 조정
  if (isDarkMode) {
    setTimeout(() => {
      const selectElement = document.getElementById('permission-email-select');
      if (selectElement) {
        selectElement.querySelectorAll('option').forEach(option => {
          option.style.backgroundColor = '#212529';
          option.style.color = '#fff';
        });
      }
    }, 300);
  }

  // 권한 추가 버튼 이벤트 리스너
  document.getElementById('add-permission-btn').addEventListener('click', () => {
    const emailSelect = document.getElementById('permission-email-select');
    const email = emailSelect.value.trim();
    const selectedRole = document.querySelector('input[name="role-option"]:checked');
    const role = selectedRole ? selectedRole.value : 'viewer';

    if (!email) {
      showToast('사용자를 선택해주세요.', 'warning');
      return;
    }

    addUserPermission(postId, email, role);
  });

  // 사용자 검색 기능
  const userSearchInput = document.getElementById('user-search');
  userSearchInput.addEventListener('input', () => {
    const searchTerm = userSearchInput.value.toLowerCase();
    const options = document.getElementById('permission-email-select').querySelectorAll('option:not(:first-child):not([disabled])');
    
    options.forEach(option => {
      const text = option.textContent.toLowerCase();
      const email = option.value.toLowerCase();
      if (text.includes(searchTerm) || email.includes(searchTerm)) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
      }
    });
  });

  // 모달이 닫힐 때 이벤트 리스너 제거
  modalElement.addEventListener('hidden.bs.modal', () => {
    modalElement.remove();
  });

  // 투어 가이드 모듈 가져오기
  import('../utils/tour-guide.js').then(module => {
    const tourGuide = module.default;
    
    // 투어 시작 버튼 이벤트 리스너 추가
    const tourButton = document.getElementById('start-permissions-tour-btn');
    if (tourButton) {
      tourButton.addEventListener('click', () => {
        // 자동 진행 모드로 투어 시작 (true)
        tourGuide.startPermissionsTour(true);
      });
    }
  }).catch(error => {
    console.error('투어 가이드 모듈 로드 실패:', error);
  });
};

/**
 * 역할에 따른 뱃지 클래스 반환
 * @param {string} role - 권한 역할
 * @returns {string} 뱃지 클래스
 */
const getBadgeClassForRole = (role) => {
  switch (role) {
    case 'admin': return 'bg-danger';
    case 'owner': return 'bg-success';
    case 'manager': return 'bg-primary';
    case 'viewer': return 'bg-info';
    default: return 'bg-secondary';
  }
};

/**
 * 역할에 따른 설명 반환
 * @param {string} role - 권한 역할
 * @returns {string} 역할 설명
 */
const getRoleDescription = (role) => {
  switch (role) {
    case 'admin': return '모든 권한 + 시스템 관리 권한';
    case 'owner': return '모든 기능 사용 및 권한 관리 가능';
    case 'manager': return '게시물 수정, 읽기, 친구 초대 가능';
    case 'viewer': return '게시물 읽기만 가능';
    default: return '권한 없음';
  }
};

/**
 * 권한 목록 불러오기
 * @param {string} postId - 게시물 ID
 */
const loadPermissionsList = async (postId) => {
  try {
    const permissionsRef = ref(database, `posts/${postId}/users`);
    const permissionsSnapshot = await get(permissionsRef);
    
    const permissionsList = document.getElementById('permissions-list');
    
    // 다크모드 감지
    const isDarkMode = document.body.classList.contains('dark-mode') || 
                       (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    if (!permissionsSnapshot.exists()) {
      permissionsList.innerHTML = `
        <div class="alert ${isDarkMode ? 'alert-dark border-secondary' : 'alert-secondary'} text-center m-0" role="alert">
          <i class="bi bi-info-circle me-2"></i>권한 정보가 없습니다.
        </div>
      `;
      return;
    }
    
    const permissionsData = permissionsSnapshot.val();
    const allPermissions = [];
    
    // 모든 권한 타입(owners, managers, viewers)에서 데이터 수집
    Object.keys(permissionsData).forEach(roleType => {
      const roleUsers = permissionsData[roleType];
      if (roleUsers && typeof roleUsers === 'object') {
        Object.keys(roleUsers).forEach(encodedEmail => {
          const userData = roleUsers[encodedEmail];
          const role = roleType.endsWith('s') ? roleType.slice(0, -1) : roleType; // 복수형 제거 (owners -> owner)
          
          allPermissions.push({
            email: userData.email || encodedEmail.replace(/,/g, '.'),
            role: role,
            displayName: userData.displayName || '사용자',
            addedAt: userData.addedAt || new Date().toISOString()
          });
        });
      }
    });
    
    // 권한 목록 HTML 생성
    if (allPermissions.length === 0) {
      permissionsList.innerHTML = `
        <div class="alert ${isDarkMode ? 'alert-dark border-secondary' : 'alert-secondary'} text-center m-0" role="alert">
          <i class="bi bi-info-circle me-2"></i>권한 정보가 없습니다.
        </div>
      `;
      return;
    }
    
    // 최신 추가 순으로 정렬
    allPermissions.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
    
    // 다크모드에 맞는 테이블 스타일 적용
    const tableClasses = isDarkMode 
      ? 'table table-hover table-dark border-secondary' 
      : 'table table-hover';
      
    let permissionsHTML = `<div class="table-responsive ${isDarkMode ? 'border-secondary' : ''}">`;
    permissionsHTML += `<table class="${tableClasses}"><tbody>`;
    
    allPermissions.forEach(permission => {
      // 다크모드에 맞는 행 스타일 적용
      const rowClass = isDarkMode ? 'text-white' : '';
      const textColorClass = isDarkMode ? 'text-white' : '';
      
      permissionsHTML += `
        <tr class="${rowClass}">
          <td class="align-middle">
            <div class="d-flex align-items-center">
              <span class="badge ${getBadgeClassForRole(permission.role)} me-2">${permission.role}</span>
              <div class="w-100 ${textColorClass}">
                <div class="${textColorClass} fw-bold">${permission.email}</div>
                <div class="${textColorClass}">${permission.displayName}</div>
              </div>
            </div>
          </td>
          <td class="text-end align-middle">
            <button class="btn btn-sm ${isDarkMode ? 'btn-outline-light' : 'btn-outline-secondary'} delete-permission-btn" 
              data-post-id="${postId}" data-email="${permission.email}" data-role="${permission.role}" 
              title="권한 삭제">
              <i class="bi bi-trash"></i>
            </button>
          </td>
        </tr>
      `;
    });
    permissionsHTML += '</tbody></table></div>';
    
    permissionsList.innerHTML = permissionsHTML;
    
    // 권한 삭제 버튼에 이벤트 리스너 추가
    document.querySelectorAll('.delete-permission-btn').forEach(button => {
      button.addEventListener('click', handleDeletePermission);
    });
    
    // 다크모드에서 추가 스타일 적용
    if (isDarkMode) {
      // 테이블 셀에 테두리 색상 적용
      const tableCells = permissionsList.querySelectorAll('td');
      tableCells.forEach(cell => {
        cell.style.borderColor = 'var(--bs-secondary)';
      });
      
      // 사용자 이름 텍스트의 스타일 적용
      const userNames = permissionsList.querySelectorAll('tr div:not(.d-flex)');
      userNames.forEach(nameElement => {
        nameElement.style.color = '#fff';
      });
      
      // 권한 삭제 버튼 호버 스타일 개선
      const deleteButtons = permissionsList.querySelectorAll('.delete-permission-btn');
      deleteButtons.forEach(button => {
        button.addEventListener('mouseover', () => {
          button.classList.remove('btn-outline-light');
          button.classList.add('btn-light', 'text-dark');
        });
        button.addEventListener('mouseout', () => {
          button.classList.remove('btn-light', 'text-dark');
          button.classList.add('btn-outline-light');
        });
      });
    }
    
  } catch (error) {
    console.error('권한 목록 로드 중 오류:', error);
    const permissionsList = document.getElementById('permissions-list');
    
    // 다크모드 감지
    const isDarkMode = document.body.classList.contains('dark-mode') || 
                      (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    permissionsList.innerHTML = `
      <div class="alert alert-danger ${isDarkMode ? 'border-secondary bg-danger bg-opacity-25' : ''} text-center m-0" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>권한 정보를 불러오는 중 오류가 발생했습니다.
      </div>
    `;
  }
};

/**
 * 사용자 권한 추가
 * @param {string} postId - 게시물 ID
 * @param {string} email - 사용자 이메일
 * @param {string} role - 권한 역할
 */
const addUserPermission = async (postId, email, role) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      showToast('권한을 추가하려면 로그인이 필요합니다.', 'warning');
      return;
    }

    // 본인이 owner인지 확인
    const currentUserEmail = currentUser.email;
    const currentUserEmailForPath = currentUserEmail.replace(/\./g, ',');
    const ownerRef = ref(database, `posts/${postId}/users/owners/${currentUserEmailForPath}`);
    const ownerSnapshot = await get(ownerRef);

    // 본인이 작성자인지 확인
    const postRef = ref(database, `posts/${postId}`);
    const postSnapshot = await get(postRef);
    const post = postSnapshot.val();
    const isAuthor = post && post.userId === currentUser.uid;

    if (!(ownerSnapshot.exists() || isAuthor)) {
      showToast('게시물 소유자만 권한을 추가할 수 있습니다.', 'error');
      return;
    }

    // 이메일 형식 검증
    if (!email.includes('@') || !email.includes('.')) {
      showToast('유효한 이메일 형식이 아닙니다.', 'error');
      return;
    }

    // 기존 사용자 정보 모두 제거 (역할 변경의 경우)
    const emailForPath = email.replace(/\./g, ',');
    const removeOwnerRef = ref(database, `posts/${postId}/users/owners/${emailForPath}`);
    const removeManagerRef = ref(database, `posts/${postId}/users/managers/${emailForPath}`);
    const removeViewerRef = ref(database, `posts/${postId}/users/viewers/${emailForPath}`);
    const removeAdminRef = ref(database, `posts/${postId}/users/admins/${emailForPath}`);

    await safeRemove(removeOwnerRef, '기존 owner 권한 제거');
    await safeRemove(removeManagerRef, '기존 manager 권한 제거');
    await safeRemove(removeViewerRef, '기존 viewer 권한 제거');
    await safeRemove(removeAdminRef, '기존 admin 권한 제거');

    // 사용자 정보 가져오기
    const usersRef = ref(database, 'users');
    const usersSnapshot = await get(usersRef);
    let displayName = email.split('@')[0]; // 기본값

    if (usersSnapshot.exists()) {
      const users = usersSnapshot.val();
      // 이메일로 사용자 찾기
      const userFound = Object.values(users).find(user => user.email === email);
      if (userFound && userFound.displayName) {
        displayName = userFound.displayName;
      }
    }

    // 새 권한 저장
    const rolePath = `${role}s`; // 복수형으로 변환 (owner -> owners)
    const permissionRef = ref(database, `posts/${postId}/users/${rolePath}/${emailForPath}`);
    
    await set(permissionRef, {
      email: email,
      role: role,
      displayName: displayName,
      addedAt: new Date().toISOString()
    });

    showToast(`${email} 사용자에게 ${role} 권한이 추가되었습니다.`, 'success');
    
    // 권한 목록 새로고침
    loadPermissionsList(postId);
    
    // 입력 필드 초기화
    document.getElementById('permission-email-select').value = '';
    
  } catch (error) {
    console.error('권한 추가 중 오류:', error);
    showToast('권한을 추가하는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 사용자 권한 삭제 핸들러
 * @param {Event} event - 클릭 이벤트
 */
const handleDeletePermission = async (event) => {
  try {
    const button = event.currentTarget;
    const postId = button.getAttribute('data-post-id');
    const email = button.getAttribute('data-email');
    const role = button.getAttribute('data-role');

    const currentUser = auth.currentUser;
    if (!currentUser) {
      showToast('권한을 삭제하려면 로그인이 필요합니다.', 'warning');
      return;
    }

    // 본인이 owner인지 확인
    const currentUserEmail = currentUser.email;
    const currentUserEmailForPath = currentUserEmail.replace(/\./g, ',');
    const ownerRef = ref(database, `posts/${postId}/users/owners/${currentUserEmailForPath}`);
    const ownerSnapshot = await get(ownerRef);

    // 본인이 작성자인지 확인
    const postRef = ref(database, `posts/${postId}`);
    const postSnapshot = await get(postRef);
    const post = postSnapshot.val();
    const isAuthor = post && post.userId === currentUser.uid;

    if (!(ownerSnapshot.exists() || isAuthor)) {
      showToast('게시물 소유자만 권한을 삭제할 수 있습니다.', 'error');
      return;
    }

    // 본인의 owner 권한은 삭제 불가
    if (email === currentUserEmail && role === 'owner') {
      showToast('본인의 소유자 권한은 삭제할 수 없습니다.', 'error');
      return;
    }

    // 권한 삭제 전 확인
    if (!confirm(`${email} 사용자의 ${role} 권한을 삭제하시겠습니까?`)) {
      return;
    }

    // 권한 삭제
    const emailForPath = email.replace(/\./g, ',');
    const rolePath = `${role}s`; // 복수형으로 변환 (owner -> owners)
    const permissionRef = ref(database, `posts/${postId}/users/${rolePath}/${emailForPath}`);
    
    await safeRemove(permissionRef, `${role} 권한 삭제`);

    showToast(`${email} 사용자의 ${role} 권한이 삭제되었습니다.`, 'success');
    
    // 권한 목록 새로고침
    loadPermissionsList(postId);
    
  } catch (error) {
    console.error('권한 삭제 중 오류:', error);
    showToast('권한을 삭제하는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 인증된 사용자 목록 불러오기
 */
const loadAuthenticatedUsers = async () => {
  try {
    const userSelect = document.getElementById('permission-email-select');
    if (!userSelect) return;
    
    // 로딩 상태 표시
    userSelect.innerHTML = '<option value="">-- 사용자 선택 --</option><option value="" disabled>사용자 목록 로딩 중...</option>';
    
    // Firebase Authentication에서 현재 사용자 정보 가져오기
    const currentUser = auth.currentUser;
    if (!currentUser) {
      userSelect.innerHTML = '<option value="">로그인이 필요합니다</option>';
      return;
    }
    
    // 현재 사용자 정보를 데이터베이스에 저장 (사용자 목록 업데이트)
    await saveCurrentUserToDatabase();
    
    // 모든 사용자 목록 가져오기
    const usersRef = ref(database, 'users');
    const snapshot = await get(usersRef);
    
    // 기본 옵션
    userSelect.innerHTML = '<option value="">-- 사용자 선택 --</option>';
    
    if (!snapshot.exists()) {
      userSelect.innerHTML += '<option value="" disabled>사용자 목록이 없습니다</option>';
      return;
    }
    
    
    // 사용자 목록 옵션 추가
    const users = snapshot.val();
    Object.entries(users).forEach(([uid, userData]) => {
      if (userData && userData.email) {
        const option = document.createElement('option');
        option.value = userData.email;
        option.textContent = `${userData.displayName || '사용자'} (${userData.email})`;
        userSelect.appendChild(option);
      }
    });
    
    // 사용자가 없는 경우
    if (userSelect.options.length <= 1) {
      userSelect.innerHTML += '<option value="" disabled>사용자 목록이 없습니다</option>';
    }
  } catch (error) {
    console.error('인증된 사용자 목록 로드 중 오류:', error);
    const userSelect = document.getElementById('permission-email-select');
    if (userSelect) {
      userSelect.innerHTML = '<option value="">-- 사용자 선택 --</option><option value="" disabled>사용자 목록 로드 오류</option>';
    }
  }
};

/**
 * 현재 로그인된 사용자 정보를 Realtime Database에 저장
 * 사용자 목록 관리를 위해 필요
 */
const saveCurrentUserToDatabase = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    const userRef = ref(database, `users/${user.uid}`);
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0],
      photoURL: user.photoURL || 'assets/default-avatar.png',
      lastLoginAt: new Date().toISOString()
    };
    
    await set(userRef, userData);
    console.log('사용자 정보가 데이터베이스에 저장되었습니다.');
    return userData;
  } catch (error) {
    console.error('사용자 정보 저장 중 오류:', error);
    return null;
  }
};

/**
 * 댓글 알림 전송 함수
 * @param {string} postId - 게시물 ID
 * @param {Object} commentData - 댓글 데이터
 */
const sendCommentNotification = async (postId, commentData) => {
  try {
    // 게시물 정보 가져오기
    const postRef = ref(database, `posts/${postId}`);
    const postSnapshot = await get(postRef);
    
    if (!postSnapshot.exists()) {
      console.log('알림 전송: 게시물이 존재하지 않음');
      return;
    }
    
    const post = postSnapshot.val();
    
    // 게시물 권한 정보 가져오기
    const usersWithPermissions = [];
    
    // 권한 유형: owners, managers, viewers, admins
    const permissionTypes = ['owners', 'managers', 'viewers', 'admins'];
    
    if (post.users) {
      permissionTypes.forEach(permType => {
        if (post.users[permType]) {
          // 각 권한 유형에 대한 사용자 추가
          Object.keys(post.users[permType]).forEach(encodedEmail => {
            const userData = post.users[permType][encodedEmail];
            const email = encodedEmail.replace(/,/g, '.');
            
            // 중복 방지를 위해 존재 확인
            const exists = usersWithPermissions.some(u => u.email === userData.email || u.email === email);
            if (!exists) {
              usersWithPermissions.push({
                email: userData.email || email,
                displayName: userData.displayName || email.split('@')[0],
                uid: userData.uid
              });
            }
          });
        }
      });
    }
    
    // 게시물 작성자 추가 (권한 목록에 없는 경우)
    if (post.author && post.author.email) {
      const exists = usersWithPermissions.some(u => u.email === post.author.email);
      if (!exists) {
        usersWithPermissions.push({
          email: post.author.email,
          displayName: post.author.displayName,
          uid: post.userId
        });
      }
    }
    
    // 권한 있는 사용자가 없으면 종료
    if (usersWithPermissions.length === 0) {
      console.log('알림 전송: 권한 있는 사용자가 없음');
      return;
    }
    
    // 채팅 알림과 동일한 엔드포인트 사용
    const sendFcmEndpoint = 'https://us-central1-kdindoor-1ac7f.cloudfunctions.net/sendChatNotification';
    
    // 게시물 제목 (내용 일부)
    const postTitle = post.content ? (post.content.length > 30 ? post.content.substring(0, 30) + '...' : post.content) : '게시물';
    const notificationTitle = `새 댓글: ${postTitle}`;
    
    // 현재 사용자를 제외한 권한 있는 사용자들에게 알림 전송
    for (const user of usersWithPermissions) {
      // 댓글 작성자는 제외
      if (user.uid === commentData.userId) continue;
      
      try {
        // UID로 사용자 정보 조회
        const userRef = ref(database, `users/${user.uid}`);
        const userSnapshot = await get(userRef);
        
        if (!userSnapshot.exists()) {
          // 이메일을 기반으로 사용자 조회
          const usersRef = ref(database, 'users');
          const usersSnapshot = await get(usersRef);
          
          if (usersSnapshot.exists()) {
            let found = false;
            const users = usersSnapshot.val();
            
            Object.keys(users).forEach(userId => {
              if (users[userId].email === user.email) {
                user.uid = userId;
                found = true;
              }
            });
            
            if (!found) {
              console.log(`${user.email} 사용자의 UID를 찾을 수 없습니다.`);
              continue;
            }
          } else {
            console.log(`${user.email} 사용자 정보를 찾을 수 없습니다.`);
            continue;
          }
        } else if (!user.uid) {
          user.uid = userSnapshot.key;
        }
        
        // 1. 사용자의 알림 설정 확인 (기본값은 활성화)
        const notificationSettingRef = ref(database, `users/${user.uid}/postNotificationSettings/${postId}`);
        const notificationSettingSnapshot = await get(notificationSettingRef);
        
        if (notificationSettingSnapshot.exists() && notificationSettingSnapshot.val() === false) {
          console.log(`${user.email}에게 알림 전송: 알림 설정 비활성화됨`);
          continue;
        }
        
        // 2. 사용자의 FCM 토큰 가져오기
        const userTokensRef = ref(database, `users/${user.uid}/fcmTokens`);
        const userTokensSnapshot = await get(userTokensRef);
        
        if (!userTokensSnapshot.exists()) {
          console.log(`${user.email} 사용자의 FCM 토큰이 없습니다.`);
          continue;
        }
        
        const tokens = userTokensSnapshot.val();
        
        if (!tokens || Object.keys(tokens).length === 0) {
          console.log(`${user.email}에게 알림 전송: FCM 토큰 없음`);
          continue;
        }
        
        // 유효한 토큰 목록 생성
        const tokenList = Object.values(tokens).filter(token => token && typeof token === 'string');
        
        if (tokenList.length === 0) {
          console.log(`${user.email}에게 알림 전송: 유효한 FCM 토큰 없음`);
          continue;
        }
        
        // 3. 데이터베이스에 알림 정보 저장
        const notificationRef = ref(database, `users/${user.uid}/notifications/${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
        const notificationData = {
          type: 'comment',
          title: notificationTitle,
          body: `${commentData.author.displayName}: ${commentData.content}`,
          postId: postId,
          commentId: commentData.id,
          senderId: commentData.userId,
          senderName: commentData.author.displayName,
          timestamp: new Date().toISOString(),
          read: false
        };
        
        await set(notificationRef, notificationData);
        console.log(`${user.email}에게 알림 데이터 저장 완료`);
        
        // 4. Cloud Functions를 통해 FCM 전송 (HTTP 호출)
        try {
          console.log('FCM 알림 전송 시작:', tokenList.length, '개의 토큰에 전송 시도');
          const response = await fetch(sendFcmEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
            },
            body: JSON.stringify({
              tokens: tokenList,
              notification: {
                title: notificationTitle,
                body: `${commentData.author.displayName}: ${commentData.content.substring(0, 100)}${commentData.content.length > 100 ? '...' : ''}`,
              },
              data: {
                postId: postId,
                commentId: commentData.id,
                senderId: commentData.userId,
                // 채팅 알림과 구분하기 위해 다른 타입 사용
                type: 'post_comment',
                clickAction: 'OPEN_POST'
              }
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log(`${user.email}에게 FCM 전송 성공:`, result);
          } else {
            const errorText = await response.text();
            console.error(`${user.email}에게 FCM 전송 실패:`, response.status, errorText);
            
            // 오류가 404인 경우 엔드포인트 확인 안내
            if (response.status === 404) {
              console.error('404 오류: Cloud Functions 엔드포인트를 확인하세요. 이 함수가 배포되었는지 확인해주세요.');
            }
          }
        } catch (error) {
          console.error(`${user.email}에게 FCM 전송 중 오류 발생:`, error.message);
        }
      } catch (userError) {
        console.error(`${user.email}에게 알림 전송 중 오류:`, userError);
      }
    }
  } catch (error) {
    console.error('댓글 알림 전송 오류:', error);
  }
};

/**
 * 선택된 파일을 UI에 표시하는 함수
 * @param {File} file - 선택된 파일
 * @param {string} chatRoomId - 채팅방 ID
 */
const showSelectedFileUI = (file, chatRoomId) => {
  if (!file) return;
  
  // 기존 파일 미리보기 제거
  const existingPreview = document.querySelector('.chat-file-preview');
  if (existingPreview) {
    existingPreview.remove();
  }
  
  // 파일 유형에 따른 아이콘 및 클래스 결정
  let fileIcon = 'bi-file-earmark';
  let fileClass = '';
  
  if (file.type.startsWith('image/')) {
    fileIcon = 'bi-file-earmark-image';
    fileClass = 'image-file';
  } else if (file.type.startsWith('video/')) {
    fileIcon = 'bi-file-earmark-play';
    fileClass = 'video-file';
  } else if (file.type.startsWith('audio/')) {
    fileIcon = 'bi-file-earmark-music';
    fileClass = 'audio-file';
  } else if (file.type === 'application/pdf') {
    fileIcon = 'bi-file-earmark-pdf';
    fileClass = 'pdf-file';
  } else if (file.type.startsWith('text/')) {
    fileIcon = 'bi-file-earmark-text';
    fileClass = 'text-file';
  }
  
  // 파일 크기 포맷팅
  const fileSize = formatFileSize(file.size);
  
  // 파일 미리보기 컨테이너 생성
  const previewContainer = document.createElement('div');
  previewContainer.className = `chat-file-preview ${fileClass}`;
  
  // 파일 정보 HTML 생성
  let previewHTML = `
    <div class="file-info">
      <i class="bi ${fileIcon} file-icon"></i>
      <div class="file-details">
        <div class="file-name">${file.name}</div>
        <div class="file-size">${fileSize}</div>
      </div>
    </div>
    <button class="btn-remove-file" title="파일 제거">
      <i class="bi bi-x"></i>
    </button>
  `;
  
  previewContainer.innerHTML = previewHTML;
  
  // 이미지 파일인 경우 미리보기 추가
  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onload = (e) => {
      previewContainer.style.backgroundImage = `url('${e.target.result}')`;
      previewContainer.classList.add('with-preview');
    };
    reader.readAsDataURL(file);
  }
  
  // 파일 제거 버튼 이벤트 리스너
  previewContainer.querySelector('.btn-remove-file').addEventListener('click', () => {
    const fileInput = document.getElementById(`chat-file-input-${chatRoomId}`);
    if (fileInput) {
      fileInput.value = '';
    }
    previewContainer.remove();
  });
  
  // 채팅 입력창 위에 파일 미리보기 삽입
  const chatInputArea = document.querySelector('.chat-input-area');
  if (chatInputArea) {
    chatInputArea.insertAdjacentElement('beforebegin', previewContainer);
  } else {
    console.error('채팅 입력 영역을 찾을 수 없습니다.');
  }
};

/**
 * 파일 크기를 읽기 쉬운 형식으로 변환
 * @param {number} bytes - 바이트 단위 파일 크기
 * @returns {string} 포맷된 파일 크기
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 사용자의 디바이스 목록을 가져오는 함수
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 디바이스 목록 배열
 */
const getUserDevices = async (userId) => {
  try {
    if (!userId) {
      console.warn('사용자 ID가 제공되지 않았습니다.');
      return [];
    }
    
    const devicesRef = ref(database, `users/${userId}/devices`);
    const snapshot = await get(devicesRef);
    
    if (snapshot.exists()) {
      const devices = snapshot.val();
      // 배열인지 확인하고, 아니면 빈 배열 반환
      if (Array.isArray(devices)) {
        return devices;
      } else if (typeof devices === 'object') {
        // 객체인 경우 값들을 배열로 변환
        return Object.values(devices);
      }
    }
    
    console.log(`사용자 ${userId}의 디바이스 목록이 없습니다.`);
    return [];
  } catch (error) {
    console.error('디바이스 목록 가져오기 실패:', error);
    return [];
  }
};

/**
 * 디바이스의 CONFIG 데이터를 가져오는 함수
 * @param {string} deviceId - 디바이스 ID
 * @returns {Promise<Object|null>} CONFIG 데이터 또는 null
 */
const getDeviceConfig = async (deviceId) => {
  try {
    if (!deviceId) {
      console.warn('디바이스 ID가 제공되지 않았습니다.');
      return null;
    }
    
    const configRef = ref(database, `DEVICE/${deviceId}/CONFIG/config`);
    const snapshot = await get(configRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      console.log(`디바이스 ${deviceId}의 CONFIG 데이터가 없습니다.`);
      return null;
    }
  } catch (error) {
    console.error(`디바이스 ${deviceId} CONFIG 가져오기 실패:`, error);
    return null;
  }
};

/**
 * 디바이스 선택창 변경 이벤트 핸들러
 * @param {Event} event - 변경 이벤트
 */
const handleDeviceSelectChange = async (event) => {
  const selectElement = event.target;
  const postId = selectElement.getAttribute('data-post-id');
  const selectedValue = selectElement.value;
  
  if (!postId) {
    console.error('게시물 ID를 찾을 수 없습니다.');
    return;
  }
  
  // 모든 탭 숨기기
  const tabContentContainer = document.getElementById(`post-tab-content-${postId}`);
  if (!tabContentContainer) {
    console.error('탭 컨테이너를 찾을 수 없습니다.');
    return;
  }
  
  const allTabs = tabContentContainer.querySelectorAll('.tab-pane');
  allTabs.forEach(tab => {
    tab.classList.remove('show', 'active');
  });
  
  if (selectedValue === 'content') {
    // 본문 탭 표시
    const contentTab = document.getElementById(`content-tab-${postId}`);
    if (contentTab) {
      contentTab.classList.add('show', 'active');
    }
  } else {
    // 디바이스 탭 표시 및 데이터 로딩
    const deviceIndex = selectElement.selectedIndex - 1; // "본문"을 제외한 인덱스
    const deviceTab = document.getElementById(`device-tab-${postId}-${deviceIndex}`);
    
    if (deviceTab) {
      deviceTab.classList.add('show', 'active');
      
      // 디바이스 데이터가 아직 로드되지 않았다면 로드
      const deviceId = deviceTab.getAttribute('data-device-id');
      if (deviceId && deviceTab.innerHTML.includes('로딩 중')) {
        try {
          showLoading(true, deviceTab.id, '디바이스 정보를 불러오는 중...');
          
          const configData = await getDeviceConfig(deviceId);
          
          if (configData) {
            // GUI 형태로 JSON 데이터 렌더링
            deviceTab.innerHTML = renderDeviceConfigGUI(deviceId, configData, postId, deviceIndex);
            
            // GUI 이벤트 리스너 설정
            setupDeviceConfigGUIListeners(deviceTab, deviceId, configData);
            
            // 명령창 버튼 이벤트 설정
            setupCommandButtonListeners(deviceTab, deviceId, postId, deviceIndex);
          } else {
            deviceTab.innerHTML = `
              <div class="text-center p-4">
                <i class="bi bi-exclamation-triangle text-warning fs-1"></i>
                <p class="mt-3 text-muted">디바이스 ${deviceId}의 설정 데이터를 찾을 수 없습니다.</p>
              </div>
            `;
          }
        } catch (error) {
          console.error('디바이스 CONFIG 로딩 실패:', error);
          deviceTab.innerHTML = `
            <div class="text-center p-4">
              <i class="bi bi-exclamation-circle text-danger fs-1"></i>
              <p class="mt-3 text-muted">디바이스 정보를 불러오는 중 오류가 발생했습니다.</p>
              <small class="text-muted">${error.message}</small>
            </div>
          `;
        } finally {
          showLoading(false, deviceTab.id);
        }
      }
    }
  }
};

/**
 * 디바이스 설정 데이터를 GUI 형태로 렌더링
 * @param {string} deviceId - 디바이스 ID
 * @param {Object} configData - 설정 데이터
 * @param {string} postId - 게시물 ID
 * @param {number} deviceIndex - 디바이스 인덱스
 * @returns {string} GUI HTML
 */
const renderDeviceConfigGUI = (deviceId, configData, postId, deviceIndex) => {
  const isDarkMode = document.body.getAttribute('data-theme') === 'dark';
  const cardClasses = isDarkMode ? 'bg-dark text-white border-secondary' : 'bg-light';
  const headerClasses = isDarkMode ? 'bg-secondary text-white' : 'bg-primary text-white';
  
  // run_mode 값 확인 (기본값: 0)
  const runMode = configData?.run_mode ?? 0;
  
  return `
    <div class="device-config-gui-container p-3">
      <!-- 헤더 -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div class="d-flex align-items-center">
          <i class="bi bi-cpu-fill fs-3 text-primary me-3"></i>
          <div>
            <h5 class="mb-1">디바이스 설정</h5>
            <small class="text-muted">${deviceId}</small>
          </div>
        </div>
        <div class="d-flex gap-2">
          <!-- 기존 버튼 그룹 -->
          <div class="btn-group">
            <button class="btn btn-sm btn-outline-secondary" id="toggle-view-${postId}-${deviceIndex}" title="JSON/GUI 보기 전환">
              <i class="bi bi-code-square"></i>
            </button>
            <button class="btn btn-sm btn-outline-primary" id="copy-config-${postId}-${deviceIndex}" title="JSON 복사">
              <i class="bi bi-clipboard"></i>
            </button>
            <button class="btn btn-sm btn-outline-success" id="save-config-${postId}-${deviceIndex}" title="설정 저장">
              <i class="bi bi-floppy"></i>
            </button>
          </div>
          
          <!-- 명령창 버튼 (드롭다운) -->
          <div class="dropdown">
            <button class="btn btn-sm btn-outline-warning dropdown-toggle" 
                    type="button" 
                    id="command-dropdown-${postId}-${deviceIndex}" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    title="명령창">
              <i class="bi bi-terminal"></i> 명령
            </button>
            <ul class="dropdown-menu" aria-labelledby="command-dropdown-${postId}-${deviceIndex}">
              <li>
                <button class="dropdown-item ${runMode === 0 ? 'active' : ''}" 
                        data-run-mode="0" 
                        data-device-id="${deviceId}"
                        id="run-mode-stop-${postId}-${deviceIndex}">
                  <i class="bi bi-stop-fill text-danger me-2"></i>정지 모드
                </button>
              </li>
              <li>
                <button class="dropdown-item ${runMode === 1 ? 'active' : ''}" 
                        data-run-mode="1" 
                        data-device-id="${deviceId}"
                        id="run-mode-auto-${postId}-${deviceIndex}">
                  <i class="bi bi-play-fill text-success me-2"></i>자동 모드
                </button>
              </li>
              <li>
                <button class="dropdown-item ${runMode === 2 ? 'active' : ''}" 
                        data-run-mode="2" 
                        data-device-id="${deviceId}"
                        id="run-mode-manual-${postId}-${deviceIndex}">
                  <i class="bi bi-hand-index-fill text-warning me-2"></i>수동 모드
                </button>
              </li>
            </ul>
          </div>
          
          <!-- 명령창 버튼 (단일 버튼) -->
          <button class="btn btn-sm btn-outline-warning" 
                  id="command-btn-${postId}-${deviceIndex}" 
                  data-device-id="${deviceId}"
                  data-post-id="${postId}"
                  data-device-index="${deviceIndex}"
                  title="명령창">
            <i class="bi bi-terminal"></i>
          </button>
          
          <!-- JSON 보기 팝업 버튼 -->
          <button class="btn btn-sm btn-outline-info" 
                  id="json-popup-${postId}-${deviceIndex}" 
                  data-device-id="${deviceId}"
                  data-config='${JSON.stringify(configData).replace(/'/g, "&#39;").replace(/"/g, "&quot;")}'
                  title="JSON 보기">
            <i class="bi bi-file-earmark-code"></i>
          </button>
        </div>
      </div>
      
      <!-- GUI 뷰 -->
      <div id="gui-view-${postId}-${deviceIndex}" class="config-gui-view">
        ${renderConfigCards(configData, `${postId}-${deviceIndex}`, '', isDarkMode)}
      </div>
      
      <!-- 명령창 뷰 (숨김) -->
      <div id="command-view-${postId}-${deviceIndex}" class="command-view d-none">
        <div class="card ${cardClasses}">
          <div class="card-header ${headerClasses}">
            <i class="bi bi-terminal me-2"></i>디바이스 제어
          </div>
          <div class="card-body text-center">
            <div class="row g-3">
              <div class="col-md-4">
                <button class="btn ${runMode === 0 ? 'btn-danger' : 'btn-outline-danger'} w-100" 
                        data-run-mode="0" 
                        data-device-id="${deviceId}"
                        id="cmd-stop-${postId}-${deviceIndex}">
                  <i class="bi bi-stop-fill me-2"></i>정지
                </button>
              </div>
              <div class="col-md-4">
                <button class="btn ${runMode === 1 ? 'btn-success' : 'btn-outline-success'} w-100" 
                        data-run-mode="1" 
                        data-device-id="${deviceId}"
                        id="cmd-auto-${postId}-${deviceIndex}">
                  <i class="bi bi-play-fill me-2"></i>자동
                </button>
              </div>
              <div class="col-md-4">
                <button class="btn ${runMode === 2 ? 'btn-warning' : 'btn-outline-warning'} w-100" 
                        data-run-mode="2" 
                        data-device-id="${deviceId}"
                        id="cmd-manual-${postId}-${deviceIndex}">
                  <i class="bi bi-hand-index-fill me-2"></i>수동
                </button>
              </div>
            </div>
            <div class="mt-3">
              <small class="text-muted">현재 모드: <strong>${runMode === 0 ? '정지' : runMode === 1 ? '자동' : '수동'}</strong></small>
            </div>
          </div>
        </div>
      </div>
      
      <!-- JSON 뷰 (숨김) -->
      <div id="json-view-${postId}-${deviceIndex}" class="config-json-view d-none">
        <div class="card ${cardClasses}">
          <div class="card-header ${headerClasses}">
            <i class="bi bi-braces me-2"></i>JSON 형식
          </div>
          <div class="card-body">
            <pre class="bg-dark text-light p-3 rounded overflow-auto" style="max-height: 400px;"><code>${JSON.stringify(configData, null, 2)}</code></pre>
          </div>
        </div>
      </div>
    </div>
  `;
};

/**
 * 설정 데이터를 카드 형태로 렌더링
 * @param {Object} data - 데이터 객체
 * @param {string} tabId - 탭 ID
 * @param {string} path - 데이터 경로
 * @param {boolean} isDarkMode - 다크모드 여부
 * @returns {string} 카드 HTML
 */
const renderConfigCards = (data, tabId, path = '', isDarkMode = false) => {
  if (!data || typeof data !== 'object') {
    return `<div class="alert alert-warning">설정 데이터가 올바르지 않습니다.</div>`;
  }
  
  const cardClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  const inputClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  
  let html = '<div class="row g-3">';
  
  Object.entries(data).forEach(([key, value], index) => {
    const currentPath = path ? `${path}.${key}` : key;
    const uniqueId = `${tabId}-${currentPath.replace(/\./g, '-')}`;
    
    html += '<div class="col-12">';
    
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // 중첩 객체인 경우 접을 수 있는 카드로
      html += `
        <div class="card ${cardClasses} mb-3">
          <div class="card-header d-flex justify-content-between align-items-center" 
               data-bs-toggle="collapse" data-bs-target="#collapse-${uniqueId}" 
               aria-expanded="true" style="cursor: pointer;">
            <div class="d-flex align-items-center">
              <i class="bi bi-folder2-open me-2 text-warning"></i>
              <strong>${key}</strong>
              <span class="badge bg-secondary ms-2">${Object.keys(value).length} 항목</span>
            </div>
            <i class="bi bi-chevron-down"></i>
          </div>
          <div class="collapse show" id="collapse-${uniqueId}">
            <div class="card-body">
              ${renderConfigCards(value, tabId, currentPath, isDarkMode)}
            </div>
          </div>
        </div>
      `;
    } else if (Array.isArray(value)) {
      // 배열인 경우
      html += `
        <div class="card ${cardClasses} mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <i class="bi bi-list-ul me-2 text-info"></i>
              <strong>${key}</strong>
              <span class="badge bg-info ms-2">${value.length} 항목</span>
            </div>
          </div>
          <div class="card-body">
            ${renderArrayItems(value, tabId, currentPath, isDarkMode)}
          </div>
        </div>
      `;
    } else {
      // 단순 값인 경우
      const valueType = typeof value;
      const iconClass = getValueIcon(valueType);
      const inputType = getInputType(valueType);
      
      html += `
        <div class="card ${cardClasses} mb-2">
          <div class="card-body p-3">
            <div class="row align-items-center">
              <div class="col-md-4">
                <label for="input-${uniqueId}" class="form-label mb-0">
                  <i class="bi ${iconClass} me-2"></i>
                  <strong>${key}</strong>
                </label>
                <small class="text-muted d-block">${valueType}</small>
              </div>
              <div class="col-md-8">
                ${renderValueInput(value, inputType, uniqueId, currentPath, inputClasses)}
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    html += '</div>';
  });
  
  html += '</div>';
  return html;
};

/**
 * 배열 항목 렌더링
 * @param {Array} array - 배열 데이터
 * @param {string} tabId - 탭 ID
 * @param {string} path - 데이터 경로
 * @param {boolean} isDarkMode - 다크모드 여부
 * @returns {string} 배열 HTML
 */
const renderArrayItems = (array, tabId, path, isDarkMode) => {
  if (array.length === 0) {
    return '<div class="text-muted text-center py-3">비어있는 배열</div>';
  }
  
  const inputClasses = isDarkMode ? 'bg-dark text-white border-secondary' : '';
  let html = '<div class="array-items">';
  
  array.forEach((item, index) => {
    const currentPath = `${path}[${index}]`;
    const uniqueId = `${tabId}-${currentPath.replace(/\[|\]|\./g, '-')}`;
    
    if (typeof item === 'object' && item !== null) {
      html += `
        <div class="card mb-2">
          <div class="card-header">
            <small class="text-muted">항목 ${index + 1}</small>
          </div>
          <div class="card-body">
            ${renderConfigCards(item, tabId, currentPath, isDarkMode)}
          </div>
        </div>
      `;
    } else {
      const valueType = typeof item;
      const inputType = getInputType(valueType);
      
      html += `
        <div class="input-group mb-2">
          <span class="input-group-text">${index + 1}</span>
          ${renderValueInput(item, inputType, uniqueId, currentPath, inputClasses)}
        </div>
      `;
    }
  });
  
  html += '</div>';
  return html;
};

/**
 * 값 입력 필드 렌더링
 * @param {any} value - 값
 * @param {string} inputType - 입력 타입
 * @param {string} uniqueId - 고유 ID
 * @param {string} path - 데이터 경로
 * @param {string} inputClasses - 입력 필드 클래스
 * @returns {string} 입력 필드 HTML
 */
const renderValueInput = (value, inputType, uniqueId, path, inputClasses) => {
  if (inputType === 'checkbox') {
    return `
      <div class="form-check form-switch d-flex align-items-center">
        <input class="form-check-input config-input" type="checkbox" 
               id="input-${uniqueId}" data-path="${path}" 
               ${value ? 'checked' : ''}>
        <label class="form-check-label ms-2" for="input-${uniqueId}">
          <span class="badge ${value ? 'bg-success' : 'bg-secondary'}">${value ? 'True' : 'False'}</span>
        </label>
      </div>
    `;
  } else if (inputType === 'textarea') {
    return `
      <textarea class="form-control config-input ${inputClasses}" 
                id="input-${uniqueId}" data-path="${path}" 
                rows="3" placeholder="값을 입력하세요...">${value}</textarea>
    `;
  } else {
    const placeholder = inputType === 'number' ? '숫자를 입력하세요...' : '값을 입력하세요...';
    return `
      <input type="${inputType}" class="form-control config-input ${inputClasses}" 
             id="input-${uniqueId}" data-path="${path}" 
             value="${value}" placeholder="${placeholder}"
             ${inputType === 'number' ? 'step="any"' : ''}>
    `;
  }
};

/**
 * 값 타입에 따른 아이콘 반환
 * @param {string} valueType - 값 타입
 * @returns {string} 아이콘 클래스
 */
const getValueIcon = (valueType) => {
  switch (valueType) {
    case 'string': return 'bi-type';
    case 'number': return 'bi-123';
    case 'boolean': return 'bi-toggle-on';
    case 'object': return 'bi-braces';
    default: return 'bi-question-circle';
  }
};

/**
 * 값 타입에 따른 입력 타입 반환
 * @param {string} valueType - 값 타입
 * @returns {string} 입력 타입
 */
const getInputType = (valueType) => {
  switch (valueType) {
    case 'number': return 'number';
    case 'boolean': return 'checkbox';
    case 'string': 
      return 'text'; // 긴 문자열인 경우 textarea로 변경할 수도 있음
    default: return 'text';
  }
};

/**
 * 디바이스 설정 GUI 이벤트 리스너 설정
 * @param {HTMLElement} deviceTab - 디바이스 탭 요소
 * @param {string} deviceId - 디바이스 ID
 * @param {Object} originalConfigData - 원본 설정 데이터
 */
const setupDeviceConfigGUIListeners = (deviceTab, deviceId, originalConfigData) => {
  // 디바이스 탭 ID에서 postId와 deviceIndex 추출
  // 형식: device-tab-{postId}-{deviceIndex}
  // postId에 대시가 포함될 수 있으므로 문자열 분리로 안전하게 처리
  const tabId = deviceTab.id;
  
  if (!tabId.startsWith('device-tab-')) {
    console.error('디바이스 탭 ID 형식이 올바르지 않습니다:', tabId);
    return;
  }
  
  // 'device-tab-' 접두사 제거 후 분리
  const withoutPrefix = tabId.replace('device-tab-', '');
  const parts = withoutPrefix.split('-');
  
  if (parts.length < 2) {
    console.error('디바이스 탭 ID에서 postId와 deviceIndex를 추출할 수 없습니다:', tabId);
    return;
  }
  
  // 마지막 부분이 deviceIndex (숫자)
  const deviceIndex = parts.pop();
  // 나머지를 다시 합쳐서 postId
  const postId = parts.join('-');
  
  // deviceIndex가 숫자인지 확인
  if (!/^\d+$/.test(deviceIndex)) {
    console.error('디바이스 인덱스가 숫자가 아닙니다:', deviceIndex);
    return;
  }
  
  const tabIdSuffix = `${postId}-${deviceIndex}`;
  
  console.log('디바이스 설정 GUI 리스너 설정:', { postId, deviceIndex, tabIdSuffix });

  // 뷰 토글 버튼
  const toggleViewBtn = deviceTab.querySelector(`#toggle-view-${tabIdSuffix}`);
  if (toggleViewBtn) {
    toggleViewBtn.addEventListener('click', () => {
      const guiView = deviceTab.querySelector(`#gui-view-${tabIdSuffix}`);
      const jsonView = deviceTab.querySelector(`#json-view-${tabIdSuffix}`);
      
      if (!guiView || !jsonView) {
        console.error('GUI 또는 JSON 뷰를 찾을 수 없습니다:', { guiView, jsonView });
        return;
      }
      
      if (guiView.classList.contains('d-none')) {
        // JSON에서 GUI로
        guiView.classList.remove('d-none');
        jsonView.classList.add('d-none');
        toggleViewBtn.innerHTML = '<i class="bi bi-code-square"></i>';
        toggleViewBtn.title = 'JSON 보기';
      } else {
        // GUI에서 JSON으로
        guiView.classList.add('d-none');
        jsonView.classList.remove('d-none');
        toggleViewBtn.innerHTML = '<i class="bi bi-grid"></i>';
        toggleViewBtn.title = 'GUI 보기';
      }
    });
  } else {
    console.warn('뷰 토글 버튼을 찾을 수 없습니다:', `#toggle-view-${tabIdSuffix}`);
  }
  
  // 복사 버튼
  const copyBtn = deviceTab.querySelector(`#copy-config-${tabIdSuffix}`);
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(JSON.stringify(originalConfigData, null, 2)).then(() => {
        showToast('설정 데이터가 클립보드에 복사되었습니다.', 'success');
      }).catch(() => {
        showToast('클립보드 복사에 실패했습니다.', 'error');
      });
    });
  } else {
    console.warn('복사 버튼을 찾을 수 없습니다:', `#copy-config-${tabIdSuffix}`);
  }
  
  // 저장 버튼
  const saveBtn = deviceTab.querySelector(`#save-config-${tabIdSuffix}`);
  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      try {
        const modifiedData = collectFormData(deviceTab);
        await saveDeviceConfig(deviceId, modifiedData);
      } catch (error) {
        console.error('설정 저장 중 오류:', error);
        showToast('설정 저장 중 오류가 발생했습니다.', 'error');
      }
    });
  } else {
    console.warn('저장 버튼을 찾을 수 없습니다:', `#save-config-${tabIdSuffix}`);
  }
  
  // run_mode 버튼들 (드롭다운 아이템)
  const runModeButtons = deviceTab.querySelectorAll(`[data-device-id="${deviceId}"][data-run-mode]`);
  runModeButtons.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const runMode = parseInt(btn.getAttribute('data-run-mode'));
      
      try {
        await updateDeviceRunMode(deviceId, runMode);
        
        // 드롭다운 아이템들의 활성 상태 업데이트
        runModeButtons.forEach(button => {
          button.classList.remove('active');
        });
        btn.classList.add('active');
        
        // 드롭다운 닫기
        const dropdown = btn.closest('.dropdown');
        if (dropdown) {
          const dropdownToggle = dropdown.querySelector('.dropdown-toggle');
          if (dropdownToggle) {
            // Bootstrap 드롭다운 인스턴스 가져와서 닫기
            const bsDropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
            if (bsDropdown) {
              bsDropdown.hide();
            }
          }
        }
        
      } catch (error) {
        console.error('실행 모드 변경 중 오류:', error);
        showToast('실행 모드 변경에 실패했습니다.', 'error');
      }
    });
  });
  
  // JSON 팝업 버튼
  const jsonPopupBtn = deviceTab.querySelector(`#json-popup-${tabIdSuffix}`);
  if (jsonPopupBtn) {
    jsonPopupBtn.addEventListener('click', () => {
      try {
        const configDataStr = jsonPopupBtn.getAttribute('data-config');
        const decodedStr = configDataStr.replace(/&#39;/g, "'").replace(/&quot;/g, '"');
        const configData = JSON.parse(decodedStr);
        const modalId = createJsonViewModal(deviceId, configData);
        
        // 모달 표시
        const modal = new bootstrap.Modal(document.getElementById(modalId));
        modal.show();
        
        // 모달이 완전히 닫힐 때 DOM에서 제거하고 백그라운드도 정리
        const modalElement = document.getElementById(modalId);
        if (modalElement) {
          modalElement.addEventListener('hidden.bs.modal', () => {
            const modalToRemove = document.getElementById(modalId);
            if (modalToRemove) {
              modalToRemove.remove();
            }
            
            // 백그라운드 오버레이 제거
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => {
              if (backdrop) {
                backdrop.remove();
              }
            });
            
            // body 클래스 정리
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
          });
        }
      } catch (error) {
        console.error('JSON 모달 생성 중 오류:', error);
        showToast('JSON 보기 중 오류가 발생했습니다.', 'error');
      }
    });
  } else {
    console.warn('JSON 팝업 버튼을 찾을 수 없습니다:', `#json-popup-${tabIdSuffix}`);
  }
  
  // 체크박스 라벨 업데이트
  deviceTab.querySelectorAll('input[type="checkbox"].config-input').forEach(checkbox => {
    checkbox.addEventListener('change', (e) => {
      const label = e.target.parentElement.querySelector('label');
      if (label) {
        label.textContent = e.target.checked ? 'True' : 'False';
      }
    });
  });
  
  // 접기/펼치기 아이콘 업데이트
  deviceTab.querySelectorAll('[data-bs-toggle="collapse"]').forEach(toggle => {
    const icon = toggle.querySelector('.bi-chevron-down, .bi-chevron-up');
    if (icon) {
      toggle.addEventListener('click', () => {
        setTimeout(() => {
          if (icon.classList.contains('bi-chevron-down')) {
            icon.classList.replace('bi-chevron-down', 'bi-chevron-up');
          } else {
            icon.classList.replace('bi-chevron-up', 'bi-chevron-down');
          }
        }, 100);
      });
    }
  });
};

/**
 * 폼 데이터 수집
 * @param {HTMLElement} container - 컨테이너 요소
 * @returns {Object} 수집된 데이터
 */
const collectFormData = (container) => {
  if (!container) {
    console.error('collectFormData: 컨테이너가 null입니다.');
    return {};
  }
  
  const data = {};
  const inputs = container.querySelectorAll('.config-input');
  
  if (!inputs || inputs.length === 0) {
    console.warn('collectFormData: 설정 입력 필드를 찾을 수 없습니다.');
    return {};
  }
  
  console.log(`collectFormData: ${inputs.length}개의 입력 필드를 발견했습니다.`);
  
  inputs.forEach((input, index) => {
    try {
      const path = input.getAttribute('data-path');
      
      if (!path) {
        console.warn(`collectFormData: 입력 필드 ${index}에 data-path 속성이 없습니다.`, input);
        return;
      }
      
      let value;
      
      if (input.type === 'checkbox') {
        value = input.checked;
      } else if (input.type === 'number') {
        const numValue = input.value === '' ? null : Number(input.value);
        value = isNaN(numValue) ? null : numValue;
      } else {
        value = input.value;
      }
      
      console.log(`collectFormData: ${path} = ${value} (type: ${typeof value})`);
      setNestedValue(data, path, value);
      
    } catch (error) {
      console.error(`collectFormData: 입력 필드 ${index} 처리 중 오류:`, error, input);
    }
  });
  
  console.log('collectFormData 결과:', data);
  return data;
};

/**
 * 중첩된 객체에 값 설정
 * @param {Object} obj - 대상 객체
 * @param {string} path - 경로 (예: "a.b.c" 또는 "a[0].b")
 * @param {any} value - 설정할 값
 */
const setNestedValue = (obj, path, value) => {
  if (!obj || typeof obj !== 'object') {
    console.error('setNestedValue: 대상 객체가 유효하지 않습니다.', obj);
    return;
  }
  
  if (!path || typeof path !== 'string') {
    console.error('setNestedValue: 경로가 유효하지 않습니다.', path);
    return;
  }
  
  try {
    const keys = path.split(/\.|\[|\]/).filter(k => k !== '');
    
    if (keys.length === 0) {
      console.error('setNestedValue: 경로에서 키를 추출할 수 없습니다.', path);
      return;
    }
    
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      
      if (!(key in current)) {
        // 다음 키가 숫자면 배열 생성, 아니면 객체 생성
        current[key] = isNaN(nextKey) ? {} : [];
      }
      
      current = current[key];
      
      if (!current || typeof current !== 'object') {
        console.error(`setNestedValue: 경로 ${key}에서 객체가 아닌 값을 발견했습니다.`, current);
        return;
      }
    }
    
    const lastKey = keys[keys.length - 1];
    current[lastKey] = value;
    
    console.log(`setNestedValue: ${path} = ${value} 설정 완료`);
    
  } catch (error) {
    console.error('setNestedValue: 값 설정 중 오류 발생:', error, { path, value });
  }
};

/**
 * 디바이스 설정 저장
 * @param {string} deviceId - 디바이스 ID
 * @param {Object} configData - 설정 데이터
 */
const saveDeviceConfig = async (deviceId, configData) => {
  if (!deviceId || typeof deviceId !== 'string') {
    console.error('saveDeviceConfig: 유효하지 않은 디바이스 ID:', deviceId);
    showToast('디바이스 ID가 유효하지 않습니다.', 'error');
    throw new Error('Invalid device ID');
  }
  
  if (!configData || typeof configData !== 'object') {
    console.error('saveDeviceConfig: 유효하지 않은 설정 데이터:', configData);
    showToast('설정 데이터가 유효하지 않습니다.', 'error');
    throw new Error('Invalid config data');
  }
  
  try {
    console.log(`saveDeviceConfig: ${deviceId} 설정 저장 시작`, configData);
    showToast('설정을 저장하는 중...', 'info');
    
    const configRef = ref(database, `DEVICE/${deviceId}/CONFIG/config`);
    
    if (!configRef) {
      throw new Error('데이터베이스 참조를 생성할 수 없습니다.');
    }
    
    await set(configRef, configData);
    
    console.log(`saveDeviceConfig: ${deviceId} 설정 저장 완료`);
    showToast('디바이스 설정이 성공적으로 저장되었습니다.', 'success');
    
  } catch (error) {
    console.error(`saveDeviceConfig: ${deviceId} 설정 저장 실패:`, error);
    
    let errorMessage = '디바이스 설정 저장에 실패했습니다.';
    
    if (error.code) {
      switch (error.code) {
        case 'PERMISSION_DENIED':
          errorMessage = '디바이스 설정을 저장할 권한이 없습니다.';
          break;
        case 'NETWORK_ERROR':
          errorMessage = '네트워크 오류로 인해 설정을 저장할 수 없습니다.';
          break;
        case 'UNAVAILABLE':
          errorMessage = '데이터베이스 서비스를 사용할 수 없습니다.';
          break;
        default:
          errorMessage = `설정 저장 실패: ${error.message || error.code}`;
      }
    } else if (error.message) {
      errorMessage = `설정 저장 실패: ${error.message}`;
    }
    
    showToast(errorMessage, 'error');
    throw error; // 호출자가 오류를 처리할 수 있도록 다시 던짐
  }
};

/**
 * JSON 보기 모달 생성
 * @param {string} deviceId - 디바이스 ID
 * @param {Object} configData - 설정 데이터
 */
const createJsonViewModal = (deviceId, configData) => {
  const isDarkMode = document.body.getAttribute('data-theme') === 'dark';
  const modalId = `jsonViewModal-${deviceId.replace(/[^a-zA-Z0-9]/g, '-')}`;
  
  // 기존 모달이 있으면 제거
  const existingModal = document.getElementById(modalId);
  if (existingModal) {
    try {
      existingModal.remove();
      console.log(`기존 모달 제거됨: ${modalId}`);
    } catch (error) {
      console.warn(`기존 모달 제거 중 오류: ${modalId}`, error);
    }
  }
  
  const modalHTML = `
    <div class="modal fade" id="${modalId}" tabindex="-1" aria-labelledby="${modalId}Label" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content ${isDarkMode ? 'bg-dark text-white' : ''}">
          <div class="modal-header ${isDarkMode ? 'border-secondary' : ''}">
            <h5 class="modal-title" id="${modalId}Label">
              <i class="bi bi-file-earmark-code me-2"></i>
              <span id="modal-title-text-${modalId}">JSON 보기</span> - ${deviceId}
            </h5>
            <div class="d-flex align-items-center gap-2">
              <!-- 뷰 토글 버튼 그룹 -->
              <div class="btn-group" role="group" aria-label="뷰 모드">
                <button type="button" class="btn btn-sm btn-outline-primary active" id="json-view-btn-${modalId}" data-view="json">
                  <i class="bi bi-code-square"></i> JSON
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary" id="gui-view-btn-${modalId}" data-view="gui">
                  <i class="bi bi-ui-checks-grid"></i> GUI
                </button>
              </div>
              <button type="button" class="btn-close ${isDarkMode ? 'btn-close-white' : ''}" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>
          <div class="modal-body">
            <!-- JSON 뷰 -->
            <div id="json-view-${modalId}" class="view-content">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <small class="text-muted">디바이스 설정 데이터</small>
                <button class="btn btn-sm btn-outline-primary" id="copy-json-${modalId}" title="JSON 복사">
                  <i class="bi bi-clipboard"></i> 복사
                </button>
              </div>
              <pre class="bg-dark text-light p-3 rounded overflow-auto" style="max-height: 500px; font-size: 0.9em;"><code id="json-content-${modalId}">${JSON.stringify(configData, null, 2)}</code></pre>
            </div>
            
            <!-- GUI 뷰 -->
            <div id="gui-view-${modalId}" class="view-content" style="display: none;">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <small class="text-muted">GUI 설정 편집기</small>
                <button class="btn btn-sm btn-outline-success" id="save-gui-config-${modalId}" title="설정 저장">
                  <i class="bi bi-check-circle"></i> 저장
                </button>
              </div>
              <div id="gui-content-${modalId}" class="config-gui-view">
                <!-- GUI 콘텐츠가 여기에 동적으로 생성됩니다 -->
              </div>
            </div>
          </div>
          <div class="modal-footer ${isDarkMode ? 'border-secondary' : ''}">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
          </div>
        </div>
      </div>
    </div>
  `;
  
  // 모달을 body에 추가
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  
  // 뷰 토글 이벤트 리스너
  const jsonViewBtn = document.getElementById(`json-view-btn-${modalId}`);
  const guiViewBtn = document.getElementById(`gui-view-btn-${modalId}`);
  const jsonView = document.getElementById(`json-view-${modalId}`);
  const guiView = document.getElementById(`gui-view-${modalId}`);
  const modalTitleText = document.getElementById(`modal-title-text-${modalId}`);
  
  // JSON 뷰 버튼 클릭
  if (jsonViewBtn) {
    jsonViewBtn.addEventListener('click', () => {
      console.log('JSON 버튼 클릭됨');
      
      // 버튼 상태 변경
      jsonViewBtn.classList.add('active');
      guiViewBtn.classList.remove('active');
      
      // 뷰 전환
      jsonView.style.display = 'block';
      guiView.style.display = 'none';
      
      // 제목 변경
      modalTitleText.textContent = 'JSON 보기';
    });
  }
  
  // GUI 뷰 버튼 클릭
  if (guiViewBtn) {
    guiViewBtn.addEventListener('click', () => {
      console.log('GUI 버튼 클릭됨');
      
      // 버튼 상태 변경
      guiViewBtn.classList.add('active');
      jsonViewBtn.classList.remove('active');
      
      // 뷰 전환
      jsonView.style.display = 'none';
      guiView.style.display = 'block';
      
      // 제목 변경
      modalTitleText.textContent = 'GUI 보기';
      
      // GUI 콘텐츠 생성 또는 업데이트
      const guiContent = document.getElementById(`gui-content-${modalId}`);
      if (guiContent) {
        console.log('GUI 콘텐츠 컨테이너 찾음:', guiContent);
        console.log('기존 innerHTML:', guiContent.innerHTML);
        
        // 기존 콘텐츠 제거하고 새로 생성
        guiContent.innerHTML = '';
        
        const isDarkMode = document.body.getAttribute('data-theme') === 'dark';
        console.log('다크모드:', isDarkMode);
        console.log('설정 데이터:', configData);
        console.log('modalId:', modalId);
        
        try {
          // renderConfigCards 함수 호출 및 결과 확인
          const cardsHTML = renderConfigCards(configData, `modal-${modalId}`, '', isDarkMode);
          console.log('renderConfigCards 결과:', cardsHTML);
          
          // 디바이스 설정 컨테이너와 동일한 GUI 편집창 사용
          const guiHTML = `
            <div class="config-gui-view">
              ${cardsHTML}
            </div>
          `;
          
          console.log('최종 GUI HTML:', guiHTML);
          guiContent.innerHTML = guiHTML;
          
          // DOM에 실제로 추가되었는지 확인
          console.log('GUI 콘텐츠 추가 후 innerHTML:', guiContent.innerHTML);
          console.log('GUI 콘텐츠 자식 요소 수:', guiContent.children.length);
          
          // GUI 입력 필드에 변경 감지 이벤트 리스너 추가
          setupGuiChangeListeners(guiContent, modalId);
          
          console.log('GUI 콘텐츠 생성 완료');
          
        } catch (error) {
          console.error('GUI 콘텐츠 생성 중 오류:', error);
          guiContent.innerHTML = `<div class="alert alert-danger">GUI 콘텐츠 생성 중 오류가 발생했습니다: ${error.message}</div>`;
        }
        
      } else {
        console.error('GUI 콘텐츠 컨테이너를 찾을 수 없음:', `gui-content-${modalId}`);
      }
    });
  } else {
    console.error('GUI 뷰 버튼을 찾을 수 없음:', `gui-view-btn-${modalId}`);
  }
  
  // JSON 복사 버튼 이벤트 리스너
  const copyBtn = document.getElementById(`copy-json-${modalId}`);
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(JSON.stringify(configData, null, 2)).then(() => {
        showToast('JSON 데이터가 클립보드에 복사되었습니다.', 'success');
      }).catch(() => {
        showToast('클립보드 복사에 실패했습니다.', 'error');
      });
    });
  }
  
  // GUI 저장 버튼 이벤트 리스너
  const saveGuiBtn = document.getElementById(`save-gui-config-${modalId}`);
  if (saveGuiBtn) {
    saveGuiBtn.addEventListener('click', async () => {
      try {
        // 저장 중 상태 표시
        const originalContent = saveGuiBtn.innerHTML;
        saveGuiBtn.disabled = true;
        saveGuiBtn.innerHTML = '<i class="bi bi-hourglass-split"></i> 저장 중...';
        
        const guiContent = document.getElementById(`gui-content-${modalId}`);
        if (guiContent) {
          const modifiedData = collectFormData(guiContent);
          await saveDeviceConfig(deviceId, modifiedData);
          
          // JSON 뷰도 업데이트
          const jsonContent = document.getElementById(`json-content-${modalId}`);
          if (jsonContent) {
            jsonContent.textContent = JSON.stringify(modifiedData, null, 2);
          }
          
          // 저장 완료 상태로 변경
          saveGuiBtn.classList.remove('btn-warning');
          saveGuiBtn.classList.add('btn-success');
          saveGuiBtn.innerHTML = '<i class="bi bi-check-circle"></i> 저장 완료';
          
          // 3초 후 원래 상태로 복원
          setTimeout(() => {
            saveGuiBtn.classList.remove('btn-success');
            saveGuiBtn.classList.add('btn-outline-success');
            saveGuiBtn.innerHTML = '<i class="bi bi-check-circle"></i> 저장';
            saveGuiBtn.disabled = false;
          }, 3000);
        }
      } catch (error) {
        console.error('GUI에서 설정 저장 중 오류:', error);
        showToast('설정 저장 중 오류가 발생했습니다.', 'error');
        
        // 오류 상태 표시
        saveGuiBtn.classList.remove('btn-warning');
        saveGuiBtn.classList.add('btn-danger');
        saveGuiBtn.innerHTML = '<i class="bi bi-x-circle"></i> 저장 실패';
        saveGuiBtn.disabled = false;
        
        // 3초 후 원래 상태로 복원
        setTimeout(() => {
          saveGuiBtn.classList.remove('btn-danger');
          saveGuiBtn.classList.add('btn-outline-success');
          saveGuiBtn.innerHTML = '<i class="bi bi-check-circle"></i> 저장';
        }, 3000);
      }
    });
  }
  
  // 모달 표시
  const modal = new bootstrap.Modal(document.getElementById(modalId));
  modal.show();
  
  // 초기 상태 확인 및 설정 (모달 표시는 호출하는 곳에서 처리)
  console.log('모달 생성 완료 - 초기 상태 확인');
  console.log('JSON 뷰 표시 상태:', jsonView ? jsonView.style.display : 'null');
  console.log('GUI 뷰 표시 상태:', guiView ? guiView.style.display : 'null');
  console.log('JSON 버튼 활성 상태:', jsonViewBtn ? jsonViewBtn.classList.contains('active') : 'null');
  console.log('GUI 버튼 활성 상태:', guiViewBtn ? guiViewBtn.classList.contains('active') : 'null');
  
  return modalId;
};

/**
 * run_mode 업데이트
 * @param {string} deviceId - 디바이스 ID
 * @param {number} runMode - 실행 모드 (0: 정지, 1: 자동, 2: 수동)
 */
const updateDeviceRunMode = async (deviceId, runMode) => {
  try {
    showToast('실행 모드를 변경하고 있습니다...', 'info');
    
    // Firebase에서 현재 설정 가져오기
    const currentConfig = await getDeviceConfig(deviceId);
    if (!currentConfig) {
      throw new Error('디바이스 설정을 찾을 수 없습니다.');
    }
    
    // run_mode 업데이트
    const updatedConfig = {
      ...currentConfig,
      run_mode: runMode
    };
    
    // Firebase에 저장
    await saveDeviceConfig(deviceId, updatedConfig);
    
    const modeNames = { 0: '정지', 1: '자동', 2: '수동' };
    showToast(`실행 모드가 "${modeNames[runMode]}"로 변경되었습니다.`, 'success');
    
    // UI 업데이트 (모든 관련 버튼들의 상태 변경)
    document.querySelectorAll(`[data-device-id="${deviceId}"][data-run-mode]`).forEach(btn => {
      if (!btn) {
        console.warn('updateDeviceRunMode: btn이 null입니다.');
        return;
      }
      
      const btnRunMode = parseInt(btn.getAttribute('data-run-mode'));
      
      if (btn.classList.contains('dropdown-item')) {
        // 드롭다운 아이템인 경우
        if (btnRunMode === runMode) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      } else {
        // 기존 버튼인 경우 (하위 호환성)
        if (btnRunMode === runMode) {
          // 활성 상태로 변경
          btn.classList.remove('btn-outline-danger', 'btn-outline-success', 'btn-outline-warning');
          if (runMode === 0) btn.classList.add('btn-danger');
          else if (runMode === 1) btn.classList.add('btn-success');
          else if (runMode === 2) btn.classList.add('btn-warning');
        } else {
          // 비활성 상태로 변경
          btn.classList.remove('btn-danger', 'btn-success', 'btn-warning');
          if (btnRunMode === 0) btn.classList.add('btn-outline-danger');
          else if (btnRunMode === 1) btn.classList.add('btn-outline-success');
          else if (btnRunMode === 2) btn.classList.add('btn-outline-warning');
        }
      }
    });
    
  } catch (error) {
    console.error('실행 모드 변경 중 오류:', error);
    showToast('실행 모드 변경에 실패했습니다.', 'error');
  }
};

/**
 * GUI 변경 감지 리스너 설정
 * @param {HTMLElement} guiContent - GUI 콘텐츠 컨테이너
 * @param {string} modalId - 모달 ID
 */
const setupGuiChangeListeners = (guiContent, modalId) => {
  if (!guiContent) return;
  
  // 모든 입력 필드에 이벤트 리스너 추가
  const inputs = guiContent.querySelectorAll('input, select, textarea');
  
  inputs.forEach(input => {
    const updateJsonPreview = () => {
      try {
        // 현재 GUI 데이터 수집
        const currentData = collectFormData(guiContent);
        
        // JSON 뷰 업데이트
        const jsonContent = document.getElementById(`json-content-${modalId}`);
        if (jsonContent) {
          jsonContent.textContent = JSON.stringify(currentData, null, 2);
        }
        
        // 변경사항이 있음을 표시
        const saveBtn = document.getElementById(`save-gui-config-${modalId}`);
        if (saveBtn && !saveBtn.classList.contains('btn-warning')) {
          saveBtn.classList.remove('btn-outline-success');
          saveBtn.classList.add('btn-warning');
          saveBtn.innerHTML = '<i class="bi bi-exclamation-triangle"></i> 저장 필요';
        }
        
      } catch (error) {
        console.error('JSON 미리보기 업데이트 중 오류:', error);
      }
    };
    
    // 토글 스위치 특별 처리
    if (input.type === 'checkbox') {
      input.addEventListener('change', (e) => {
        console.log('토글 스위치 변경됨:', e.target.checked);
        
        // 배지 업데이트
        const label = e.target.nextElementSibling;
        if (label && label.classList.contains('form-check-label')) {
          const badge = label.querySelector('.badge');
          if (badge) {
            if (e.target.checked) {
              badge.textContent = 'True';
              badge.className = 'badge bg-success';
            } else {
              badge.textContent = 'False';
              badge.className = 'badge bg-secondary';
            }
            
            // 배지 애니메이션 효과
            badge.style.animation = 'badgeSuccess 0.3s ease';
            setTimeout(() => {
              badge.style.animation = '';
            }, 300);
          }
        }
        
        updateJsonPreview();
      });
    } else {
      // 일반 입력 필드
      input.addEventListener('input', updateJsonPreview);
      input.addEventListener('change', updateJsonPreview);
    }
  });
  
  console.log(`GUI 변경 감지 리스너 설정 완료: ${inputs.length}개 입력 필드`);
};

/**
 * 명령창 버튼 이벤트 설정
 * @param {HTMLElement} deviceTab - 디바이스 탭 요소
 * @param {string} deviceId - 디바이스 ID
 * @param {string} postId - 게시물 ID
 * @param {string} deviceIndex - 디바이스 인덱스
 */
const setupCommandButtonListeners = (deviceTab, deviceId, postId, deviceIndex) => {
  const tabIdSuffix = `${postId}-${deviceIndex}`;
  let runModeListener = null; // 실시간 리스너 참조 저장
  
  // 실시간 run_mode 모니터링 시작
  const startRunModeMonitoring = () => {
    if (runModeListener) {
      console.log('이미 run_mode 모니터링이 활성화되어 있습니다.');
      return;
    }
    
    const runModeRef = ref(database, `DEVICE/${deviceId}/CONFIG/config/run_mode`);
    
    runModeListener = onValue(runModeRef, (snapshot) => {
      const currentRunMode = snapshot.val();
      console.log(`${deviceId} run_mode 실시간 업데이트:`, currentRunMode);
      
      if (currentRunMode !== null && currentRunMode !== undefined) {
        updateCommandViewUI(deviceTab, tabIdSuffix, deviceId, currentRunMode);
      }
    }, (error) => {
      console.error(`${deviceId} run_mode 모니터링 오류:`, error);
    });
    
    console.log(`${deviceId} run_mode 실시간 모니터링 시작`);
  };
  
  // 실시간 run_mode 모니터링 중지
  const stopRunModeMonitoring = () => {
    if (runModeListener) {
      const runModeRef = ref(database, `DEVICE/${deviceId}/CONFIG/config/run_mode`);
      off(runModeRef, 'value', runModeListener);
      runModeListener = null;
      console.log(`${deviceId} run_mode 실시간 모니터링 중지`);
    }
  };
  
  // 명령창 뷰 UI 업데이트
  const updateCommandViewUI = (deviceTab, tabIdSuffix, deviceId, runMode) => {
    const commandView = deviceTab.querySelector(`#command-view-${tabIdSuffix}`);
    if (!commandView || commandView.classList.contains('d-none')) {
      return; // 명령창이 보이지 않으면 업데이트하지 않음
    }
    
    // 명령창 뷰의 버튼들 상태 업데이트
    const commandModeButtons = deviceTab.querySelectorAll(`[id^="cmd-"][data-device-id="${deviceId}"]`);
    commandModeButtons.forEach(button => {
      const btnRunMode = parseInt(button.getAttribute('data-run-mode'));
      if (btnRunMode === runMode) {
        // 활성 상태로 변경
        button.classList.remove('btn-outline-danger', 'btn-outline-success', 'btn-outline-warning');
        if (runMode === 0) button.classList.add('btn-danger');
        else if (runMode === 1) button.classList.add('btn-success');
        else if (runMode === 2) button.classList.add('btn-warning');
      } else {
        // 비활성 상태로 변경
        button.classList.remove('btn-danger', 'btn-success', 'btn-warning');
        if (btnRunMode === 0) button.classList.add('btn-outline-danger');
        else if (btnRunMode === 1) button.classList.add('btn-outline-success');
        else if (btnRunMode === 2) button.classList.add('btn-outline-warning');
      }
    });
    
    // 현재 모드 텍스트 업데이트
    const modeText = deviceTab.querySelector(`#command-view-${tabIdSuffix} .text-muted strong`);
    if (modeText) {
      modeText.textContent = runMode === 0 ? '정지' : runMode === 1 ? '자동' : '수동';
    }
    
    // 드롭다운 메뉴의 버튼들도 업데이트
    const dropdownButtons = deviceTab.querySelectorAll(`[data-device-id="${deviceId}"][data-run-mode].dropdown-item`);
    dropdownButtons.forEach(button => {
      const btnRunMode = parseInt(button.getAttribute('data-run-mode'));
      if (btnRunMode === runMode) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  };
  
  // 명령창 버튼
  const commandBtn = deviceTab.querySelector(`#command-btn-${tabIdSuffix}`);
  if (commandBtn) {
    commandBtn.addEventListener('click', () => {
      const guiView = deviceTab.querySelector(`#gui-view-${tabIdSuffix}`);
      const commandView = deviceTab.querySelector(`#command-view-${tabIdSuffix}`);
      const jsonView = deviceTab.querySelector(`#json-view-${tabIdSuffix}`);
      
      if (!guiView || !commandView) {
        console.error('GUI 또는 명령창 뷰를 찾을 수 없습니다:', { guiView, commandView });
        return;
      }
      
      if (commandView.classList.contains('d-none')) {
        // GUI에서 명령창으로
        guiView.classList.add('d-none');
        if (jsonView) jsonView.classList.add('d-none');
        commandView.classList.remove('d-none');
        commandBtn.classList.remove('btn-outline-warning');
        commandBtn.classList.add('btn-warning');
        commandBtn.title = 'GUI 보기로 돌아가기';
        
        // 실시간 모니터링 시작
        startRunModeMonitoring();
      } else {
        // 명령창에서 GUI로
        commandView.classList.add('d-none');
        guiView.classList.remove('d-none');
        commandBtn.classList.remove('btn-warning');
        commandBtn.classList.add('btn-outline-warning');
        commandBtn.title = '명령창';
        
        // 실시간 모니터링 중지
        stopRunModeMonitoring();
      }
    });
  }
  
  // 명령창 뷰의 정지/자동/수동 버튼들
  const commandModeButtons = deviceTab.querySelectorAll(`[id^="cmd-"][data-device-id="${deviceId}"]`);
  commandModeButtons.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const runMode = parseInt(btn.getAttribute('data-run-mode'));
      
      try {
        await updateDeviceRunMode(deviceId, runMode);
        // 실시간 리스너가 자동으로 UI를 업데이트하므로 여기서는 별도 처리 불필요
        
      } catch (error) {
        console.error('실행 모드 변경 중 오류:', error);
        showToast('실행 모드 변경에 실패했습니다.', 'error');
      }
    });
  });
  
  // 페이지 언로드 시 리스너 정리
  const cleanup = () => {
    stopRunModeMonitoring();
  };
  
  // 전역 정리 함수에 등록 (페이지 새로고침/닫기 시)
  if (!window.deviceListenersCleanup) {
    window.deviceListenersCleanup = [];
    
    window.addEventListener('beforeunload', () => {
      window.deviceListenersCleanup.forEach(cleanupFn => cleanupFn());
    });
  }
  
  window.deviceListenersCleanup.push(cleanup);
};
  